import { createCompoundInterestTool, createEMITool } from "../factories";
import type { ToolConfig } from "../types";

export const savingsTools: ToolConfig[] = [
  // 1. FD Calculator
  createCompoundInterestTool({
    slug: "fd-calculator",
    category: "savings",
    title: "Fixed Deposit Calculator",
    shortTitle: "FD Maturity",
    description: "Instant FD maturity: guaranteed returns, DICGC safe",
    keywords: [
      "fixed deposit",
      "fd calculator",
      "bank deposit",
      "savings account",
      "guaranteed return",
    ],
    defaultAmount: 500000,
    maxAmount: 100000000,
    defaultRate: 6,
    defaultYears: 3,
    maxYears: 20,
    compoundingFrequency: 4,
    whatIs:
      "Fixed Deposit (FD) is India's safest savings instrument, offering guaranteed returns backed by the Deposit Insurance and Credit Guarantee Corporation (DICGC) up to ₹5 lakh per bank per person. When you invest in an FD, your principal is locked for a chosen tenure ranging from 3 months to 10 years, during which the bank pays a fixed interest rate. Interest compounds quarterly, meaning you earn 'interest on interest,' accelerating wealth growth. Banks typically offer 5.5-7% p.a. for general public, with senior citizens aged 60+ receiving 0.5-1% higher rates. FDs are ideal for conservative savers who prioritize capital safety over high returns, individuals building emergency funds, those nearing retirement, and anyone wanting predictable, zero-risk income. Many use FDs as a 'risk-free' component of a diversified portfolio alongside equities or mutual funds. The biggest advantage: complete certainty about your returns, making financial planning straightforward and stress-free.",
    sections: [
      {
        title: "Who Should Use This Calculator?",
        body: "This calculator is perfect for salaried employees, retirees, business owners, and anyone seeking guaranteed returns on surplus capital. If you have bonus money, inheritance, or accumulated savings you want to protect while earning decent returns, FD is your answer. Even young investors should park emergency funds (3-6 months expenses) in FD for safety and liquidity.",
      },
      {
        title: "How FD Compounding Works in India",
        body: "Most banks in India compound FD interest quarterly (4 times yearly), though some offer monthly or annual compounding. Quarterly compounding means the bank calculates interest every 3 months and adds it to your principal, so the next quarter's interest is calculated on the larger amount. This compounds automatically—you don't have to do anything. Over 5 years, quarterly compounding significantly beats annual compounding, especially on large amounts.",
      },
      {
        title: "Key Rules & DICGC Insurance",
        body: "The DICGC (government insurer) protects deposits up to ₹5 lakh per bank per depositor. If you have ₹10 lakh, spread it: ₹5L in Bank A, ₹5L in Bank B. Both are fully protected. Principal and accrued interest both covered. This makes FD one of India's safest investments—safer than even Government Securities for most depositors.",
      },
      {
        title: "Liquidity & Early Withdrawal Penalties",
        body: "While FDs are designed for lock-in, most banks allow early withdrawal with penalties. Break before 7 days: lose all interest. 7 days to 1 month: 0.5% penalty. 1-12 months: 1% penalty. Beyond 12 months: standard rate minus 1-1.5%. After 7-12 months, breaking makes sense only in emergencies.",
      },
      {
        title: "Tax Implications & Section 80C",
        body: "FD interest is fully taxable as income. Banks deduct TDS (Tax Deducted at Source) at 20-30% if annual interest exceeds ₹10K. Use Form 15G (under senior citizens) or Form 15H to avoid TDS if your total income is below the taxable limit. Tax-saving FDs (5-year lock-in) offer Section 80C deduction, though standard FDs don't. After-tax FD returns are typically 4-5% for mid-income earners.",
      },
    ],
    faqs: [
      {
        question: "What interest rates do banks offer on FD?",
        answer:
          "Typically 5.5-7% p.a. for general public. Senior citizens (60+) get 6-7.5%. Higher rates for longer tenures. Check individual bank websites; rates vary monthly.",
      },
      {
        question: "Is FD 100% safe?",
        answer:
          "Yes. Deposits insured up to ₹5L per bank by DICGC. If you have ₹10L, keep ₹5L max per bank across 2+ banks. Principal + interest both protected.",
      },
      {
        question: "Can I break FD before maturity?",
        answer:
          "Yes, but with penalty. Break <7 days: no interest. Break 7 days-1 month: 0.5% penalty. 1-12 months: 1% penalty. >12 months: standard rate minus 1-1.5%. Avoid early break; lose returns.",
      },
      {
        question: "Is FD interest taxed?",
        answer:
          "Yes, fully taxable as income. Banks deduct TDS (20-30%) if interest exceeds ₹10K/year. Use Form 15G/15H to avoid TDS if your total income is below taxable limit.",
      },
      {
        question: "Should I ladder FDs or put all in one?",
        answer:
          "Ladder is better. Invest ₹10L as: ₹2L in 1yr FD, ₹2L in 2yr, ₹2L in 3yr, ₹2L in 4yr, ₹2L in 5yr. Matures sequentially for liquidity without breaking any FD.",
      }
    ],
    relatedSlugs: [
      "savings/rd-calculator",
      "savings/fd-interest-calculator",
      "savings/compound-interest-calculator",
      "savings/fd-vs-rd-comparison",
    ],
  }),

  // 2. RD Calculator
  createEMITool({
    slug: "rd-calculator",
    category: "savings",
    title: "Recurring Deposit Calculator",
    shortTitle: "RD Maturity",
    description:
      "Calculate RD maturity with monthly deposits and quarterly compounding",
    keywords: [
      "recurring deposit",
      "rd calculator",
      "monthly savings",
      "savings scheme",
      "small savings",
    ],
    inputs: [
      {
        type: "number",
        key: "monthlyDeposit",
        label: "Monthly Deposit Amount",
        prefix: "₹",
        min: 100,
        max: 1000000,
        step: 100,
        default: 10000,
      },
      {
        type: "number",
        key: "months",
        label: "Duration",
        suffix: "months",
        min: 3,
        max: 240,
        step: 3,
        default: 60,
      },
      {
        type: "number",
        key: "rate",
        label: "Interest Rate",
        suffix: "% p.a.",
        min: 1,
        max: 20,
        step: 0.1,
        default: 5.5,
      }
    ],
    calculate(v) {
      const monthly = v.monthlyDeposit as number;
      const months = v.months as number;
      const annual = v.rate as number;
      const quarterlyRate = annual / 100 / 4;

      let rdValue = 0;

      for (let m = 1; m <= months; m++) {
        const quartersRemaining = (months - m) / 3;
        rdValue +=
          monthly *
          Math.pow(1 + quarterlyRate, quartersRemaining);
      }

      const totalDeposited = monthly * months;
      const interestEarned = rdValue - totalDeposited;

      return {
        maturityAmount: Math.round(rdValue),
        totalDeposited: Math.round(totalDeposited),
        interestEarned: Math.round(interestEarned),
        months: months,
        years: Math.round((months / 12) * 10) / 10,
      };
    },
    resultFields: [
      {
        key: "maturityAmount",
        label: "RD Maturity Amount",
        format: "currency",
        isPrimary: true,
      },
      {
        key: "totalDeposited",
        label: "Total Amount Deposited",
        format: "currency",
      },
      {
        key: "interestEarned",
        label: "Interest Earned",
        format: "currency",
      }
    ],
    content: {
      whatIs:
        "Recurring Deposit (RD) is India's most popular disciplined savings scheme, designed for salaried individuals who earn monthly income. You commit to depositing a fixed amount (₹100 minimum, typically ₹1,000-10,000) every month for a tenure of 3 months to 10 years. Unlike a Lumpsum Fixed Deposit, RD doesn't require you to have a large amount upfront—you build it gradually through consistent monthly deposits. Banks offer 5-6.5% p.a. on RDs, with interest compounding quarterly on the growing balance. This means each monthly deposit earns interest from the moment it's deposited, for a decreasing number of months. By maturity, you've contributed principal plus earned interest, resulting in a larger corpus. RDs are ideal for young professionals starting to save, homemakers managing household budgets, business owners with variable monthly income, and retirees supplementing fixed income. Many banks offer 'step-up RD' where your monthly amount increases annually, creating an inflation-adjusted savings plan. RD teaches the discipline of automatic monthly saving—set it and forget it via auto-debit from your salary account.",
      faqs: [
        {
          question:
            "What's the difference between RD and FD?",
          answer:
            "RD: multiple monthly deposits, lower returns (5-6%), good for savers. FD: single lumpsum, higher returns (6-7%), good for existing capital. RD is like SIP for savings.",
        },
        {
          question:
            "Can I increase RD monthly deposit?",
          answer:
            "Some banks allow 'step-up RD' where amount increases annually. Most fixed RDs: same amount each month. Check your bank.",
        },
        {
          question:
            "Is RD better than keeping money in savings account?",
          answer:
            "Absolutely. Savings account: 2-4% return. RD: 5-6% return. Same safety (DICGC insured). RD earns ₹10-15K more annually on ₹2L deposits. No reason not to use RD.",
        },
        {
          question:
            "What if I miss a monthly RD deposit?",
          answer:
            "Penalty varies: ₹10-50/month typically. Missing deposits also reduces final maturity. Try not to miss; set up auto-debit from your salary account.",
        },
        {
          question:
            "Can I withdraw RD prematurely?",
          answer:
            "Yes, after 3-6 months (depends on bank). Penalty is lower for RD than FD: usually 1% of interest or 0.5% of balance. Avoid early withdrawal if possible.",
        }
      ],
      sections: [
        {
          title: "Who Should Use RD?",
          body: "RD is perfect for salaried employees earning monthly income who want to save systematically without discipline burden. Homemakers managing household expenses, young professionals starting out, self-employed with irregular but recurring income, and anyone who finds lumpsum savings difficult. RD converts spending potential into wealth automatically.",
        },
        {
          title: "How RD Calculation Works",
          body: "Each monthly deposit earns interest for the remaining tenure. Your first deposit earns for the full period; last deposit earns only for one month. The calculator compounds quarterly, so interest on interest accelerates growth. Example: ₹10K/month for 5 years (60 months) at 5.5% grows to ~₹6.5L.",
        },
        {
          title: "RD vs FD: Which to Choose",
          body: "RD if you earn monthly salary and don't have lumpsum. FD if you have bonus or savings to invest immediately. RD gives lower returns (5-6%) than FD (6-7%) because money is invested gradually. Both are DICGC insured up to ₹5L and absolutely safe.",
        },
        {
          title: "Making RD Automatic",
          body: "Set up auto-debit from your salary account on the day you receive salary. Most banks offer zero penalty for regular deposits. Missing deposits? Banks usually allow, but maturity gets reduced. Best practice: keep account funded and let bank deduct automatically.",
        },
        {
          title: "Step-Up RD for Inflation Adjustment",
          body: "Some banks offer 'step-up RD' where monthly deposit increases by a fixed percentage (5-10%) annually. Useful for professionals whose income grows yearly. Instead of fixed ₹10K monthly, do ₹10K→₹10.5K→₹11K progressively. Results in 10-15% higher final maturity.",
        },
      ],
      relatedSlugs: [
        "savings/fd-calculator",
        "savings/fd-vs-rd-comparison",
        "investment/sip-calculator",
      ],
    },
    }),

  // 3. FD Interest Calculator
  createEMITool({
    slug: "fd-interest-calculator",
    category: "savings",
    title: "FD Interest Calculator",
    shortTitle: "FD Interest Only",
    description: "Calculate only the interest earned on FD, separately from principal",
    keywords: [
      "fd interest",
      "fixed deposit interest",
      "interest earnings",
      "fd returns",
    ],
    inputs: [
      {
        type: "number",
        key: "principal",
        label: "FD Principal Amount",
        prefix: "₹",
        min: 10000,
        max: 100000000,
        step: 10000,
        default: 500000,
      },
      {
        type: "number",
        key: "rate",
        label: "Annual Interest Rate",
        suffix: "% p.a.",
        min: 1,
        max: 15,
        step: 0.1,
        default: 6.5,
      },
      {
        type: "number",
        key: "years",
        label: "FD Tenure",
        suffix: "years",
        min: 0.25,
        max: 20,
        step: 0.25,
        default: 3,
      },
      {
        type: "select",
        key: "compounding",
        label: "Compounding Frequency",
        options: [
          { value: "quarterly", label: "Quarterly (Most Common)" },
          { value: "monthly", label: "Monthly" },
          { value: "annual", label: "Annual" },
        ],
        default: "quarterly",
      }
    ],
    calculate(v) {
      const principal = v.principal as number;
      const rate = (v.rate as number) / 100;
      const years = v.years as number;
      const compounding = v.compounding as string;

      let freq = 4;
      if (compounding === "monthly") freq = 12;
      else if (compounding === "annual") freq = 1;

      const maturityAmount = principal * Math.pow(1 + rate / freq, freq * years);
      const interest = maturityAmount - principal;

      return {
        principal: Math.round(principal),
        interestEarned: Math.round(interest),
        maturityAmount: Math.round(maturityAmount),
        annualInterest:
          Math.round((interest / years) * 100) / 100,
        rate: v.rate as number,
      };
    },
    resultFields: [
      {
        key: "interestEarned",
        label: "Total Interest Earned",
        format: "currency",
        isPrimary: true,
      },
      {
        key: "maturityAmount",
        label: "Total Maturity Amount",
        format: "currency",
      },
      {
        key: "annualInterest",
        label: "Average Annual Interest",
        format: "currency",
      }
    ],
    content: {
      whatIs:
        "FD Interest Calculator helps you understand exactly how much 'free money' your Fixed Deposit will earn. While FD maturity calculators show final amount, this tool isolates the interest component—the extra earnings beyond your principal. On a ₹10 lakh FD at 6.5% for 5 years, your principal stays ₹10 lakh, but interest earned is ~₹3.7 lakh. This interest separation is crucial for tax planning: in India, all FD interest is fully taxable as income, and if annual interest exceeds ₹10,000, banks deduct TDS (Tax Deducted at Source) at 20-30%. Understanding your interest liability helps you plan around the tax impact. This calculator also shows 'average annual interest'—for budgeting purposes or if you need monthly income projections. Many investors use this to assess whether their FD returns meet their retirement income needs or if they should deploy capital elsewhere for better returns.",
      faqs: [
        {
          question:
            "How much interest tax do I pay on FD?",
          answer:
            "Interest is fully taxable as income. TDS: 20-30% deducted by bank if interest >₹10K/year. Final tax depends on your slab (10-42.94%). Use Form 15G to avoid TDS if income is below taxable limit.",
        },
        {
          question:
            "Is it better to reinvest FD interest or take it out?",
          answer:
            "Reinvest for compounding benefit. If FD pays ₹30K interest annually, new FD with interest = ₹30K more grows. Over 10 years, significant difference due to compounding.",
        },
        {
          question:
            "Can I claim FD interest as a loss against other income?",
          answer:
            "No. Interest is income, not deductible. But if FD is in spouse's name (lower-income), interest taxed at lower rate. Tax planning: split large FD across both spouses.",
        },
        {
          question:
            "Do senior citizens get tax benefits on FD interest?",
          answer:
            "Yes. Section 80TTB: up to ₹50K interest from bank/post office deposits is deductible if age 60+. Combined with 80TTA (younger): ₹10K deduction.",
        },
        {
          question:
            "Should I choose monthly interest payout vs cumulative?",
          answer:
            "Cumulative (reinvest): higher maturity. Monthly payout: regular income, good for retirees. Tax-wise: both same. Choose based on liquidity need.",
        }
      ],
      sections: [
        {
          title: "Why Separating Interest Matters for Tax Planning",
          body: "In India, interest on FD is fully taxable. Knowing your exact interest liability helps you plan around TDS deductions. If your total annual income (from salary, investments, etc.) is below ₹5 lakh (₹5 lakh for women, ₹7.5 lakh for senior citizens), you can avoid TDS using Form 15G/15H. Without knowing your interest amount, you can't estimate this.",
        },
        {
          title: "TDS Rules on FD Interest",
          body: "TDS is deducted at 20-30% if annual interest exceeds ₹10,000. If your FD generates ₹25K interest, TDS of ~₹5-7K is deducted automatically. This compounds over multiple FDs. Using Form 15G prevents TDS, but only if your total income is below taxable limit. Use this calculator to estimate total interest from all your FDs combined.",
        },
        {
          title: "Comparing Compounding Frequencies",
          body: "Quarterly compounding (most common in India) beats annual compounding by ~0.1-0.2% annually. Seems small, but over 10 years on large amounts (₹50L), it's ₹50K+ extra. This calculator shows the cumulative benefit of quarterly vs annual or monthly compounding.",
        },
        {
          title: "After-Tax vs Pre-Tax Returns",
          body: "The interest shown is pre-tax. After paying 30% income tax on interest, your effective return drops significantly. On 6.5% pre-tax return, after 30% tax on interest portion, your after-tax return is ~5.5%. This is why many investors prefer tax-saving schemes like PPF or SSY.",
        },
        {
          title: "Using Interest for Retirement Income",
          body: "If you're retired, knowing FD interest helps estimate your monthly income. ₹50L FD at 6.5% generates ~₹3.25L annually, or ~₹27K monthly. Compare this against your monthly expense to see if you need additional income sources or can reduce capital deployment.",
        },
      ],
      relatedSlugs: [
        "savings/fd-calculator",
        "savings/simple-interest-calculator",
        "savings/compound-interest-calculator",
      ],
    },
  }),

  // 4. Compound Interest Calculator
  createCompoundInterestTool({
    slug: "compound-interest-calculator",
    category: "savings",
    title: "Compound Interest Calculator",
    shortTitle: "Compound Interest",
    description: "Calculate wealth growth through compounding power",
    keywords: [
      "compound interest",
      "compounding",
      "interest calculation",
      "wealth growth",
    ],
    defaultAmount: 100000,
    maxAmount: 100000000,
    defaultRate: 8,
    defaultYears: 10,
    maxYears: 50,
    compoundingFrequency: 4,
    whatIs:
      "Compound Interest is often called the 'eighth wonder of the world' for good reason—it's the single most powerful tool for wealth creation. Compound interest means 'interest earning its own interest.' When you invest ₹1 lakh at 8% annually, after year one you have ₹1.08 lakh. Year two, you earn 8% on ₹1.08 lakh (not just the original ₹1 lakh), resulting in ₹1.1664 lakh. This accelerating effect means your money grows exponentially, not linearly. Over 35 years, ₹1 lakh at 8% compounds to ₹21.6 lakh—a 21.6x multiplication! The magic is that time is your biggest ally. Starting early (even with small amounts) beats starting late with large amounts. A 25-year-old investing ₹1L/year for 10 years gets far better results than someone starting at 35, even if the 35-year-old invests more per year. This calculator shows you the power of starting early and the importance of choosing even 1% higher return rates. In India, understanding compounding is critical since safe instruments like FD (6-7%) compound slower than equities (12-15%), but both compound meaningfully over decades.",
    sections: [
      {
        title: "Who Should Use This Calculator?",
        body: "Anyone planning long-term wealth creation: students starting SIPs, young professionals investing bonuses, retirees planning corpus adequacy, parents funding children's education, and anyone considering when to start investing. If you're considering starting investments 'later,' run this calculator to see the difference versus starting now.",
      },
      {
        title: "The Power of Time in Compounding",
        body: "Starting at 25 vs 35 (a 10-year delay) can reduce final wealth by 30-50% even if later investment rate is higher. This is the biggest leverage in wealth building. Compounding works best over 30+ years. Even 5% returns compound to significant wealth given sufficient time. A 15-year-old investing ₹10K/year for 50 years becomes a multi-crore-rupee portfolio.",
      },
      {
        title: "Rate Matters More Than Amount Over Time",
        body: "Investing ₹50K annually at 5% beats ₹100K at 3% over 30 years. The rate difference compounds cumulatively. A 1% increase in annual return (6% vs 7%) can translate to ₹20-30 lakh additional wealth on ₹50L investment over 20 years. This is why choosing the right investment instrument is critical.",
      },
      {
        title: "Compounding Frequency Impact",
        body: "Quarterly compounding beats annual compounding by ~0.1-0.2% yearly. Semi-annual vs quarterly: minimal difference. Daily compounding (used in savings accounts) adds ~0.05% vs quarterly. For wealth planning, rate matters far more than frequency. A 6.5% quarterly FD beats 6% daily compounding.",
      },
      {
        title: "How to Maximize Compounding in India",
        body: "Invest in instruments with full tax benefits: PPF (₹1.5L/year, Section 80C, tax-free returns), SSY for daughters (₹1.5L/year, 8.2%, tax-free), RDs/FDs (5-7% with TDS), and equity SIPs (long-term capital gains: 0% tax if held 1+ year after 2023). Maximize tax-free compounding through 80C schemes for the first ₹1.5L, then deploy additional capital in equities.",
      },
    ],
    faqs: [
      {
        question:
          "How powerful is compounding really?",
        answer:
          "Exponentially powerful. ₹1L at 8% p.a.: 10 years = ₹2.16L, 20 years = ₹4.66L, 30 years = ₹10.06L. Each decade roughly doubles your money. Time is your biggest ally.",
      },
      {
        question:
          "Is 1% difference in return rate significant?",
        answer:
          "Yes! 7% vs 8% over 30 years: ₹7.61L vs ₹10.06L. That 1% difference = ₹2.45L extra! Always compare rates and choose highest safe return.",
      },
      {
        question:
          "Does compounding work with inflation?",
        answer:
          "Your nominal value compounds, but real (purchasing power) compounds slower. At 8% return and 5% inflation, real compounding = 2.86%. Plan for real returns, not nominal.",
      },
      {
        question:
          "When should I start investing to benefit from compounding?",
        answer:
          "Now. The earlier you start, the longer compounding works. Starting at 25 vs 35 adds 15+ years of compounding—easily ₹50-100L difference by retirement.",
      },
      {
        question:
          "Can I compound money without investing?",
        answer:
          "In savings account (2-4% compound quarterly), yes but slow. For meaningful compounding, invest in FD (6-7%), RD (5-6%), mutual funds (10-12%), or equities (12-15%).",
      }
    ],
    relatedSlugs: [
      "savings/compound-interest-calculator",
      "savings/simple-interest-calculator",
      "investment/sip-calculator",
    ],
  }),

  // 5. Simple Interest Calculator
  createEMITool({
    slug: "simple-interest-calculator",
    category: "savings",
    title: "Simple Interest Calculator",
    shortTitle: "Simple Interest",
    description:
      "Calculate simple interest (non-compounding) on loans and savings",
    keywords: [
      "simple interest",
      "interest calculation",
      "loan interest",
      "savings interest",
    ],
    inputs: [
      {
        type: "number",
        key: "principal",
        label: "Principal Amount",
        prefix: "₹",
        min: 1000,
        max: 100000000,
        step: 1000,
        default: 100000,
      },
      {
        type: "number",
        key: "rate",
        label: "Annual Interest Rate",
        suffix: "% p.a.",
        min: 0.1,
        max: 30,
        step: 0.1,
        default: 8,
      },
      {
        type: "number",
        key: "years",
        label: "Time Period",
        suffix: "years",
        min: 0.1,
        max: 50,
        step: 0.1,
        default: 5,
      }
    ],
    calculate(v) {
      const principal = v.principal as number;
      const rate = (v.rate as number) / 100;
      const years = v.years as number;

      const simpleInterest = principal * rate * years;
      const totalAmount = principal + simpleInterest;

      return {
        principal: Math.round(principal),
        simpleInterest: Math.round(simpleInterest),
        totalAmount: Math.round(totalAmount),
        rate: v.rate as number,
        years: years,
      };
    },
    resultFields: [
      {
        key: "simpleInterest",
        label: "Simple Interest",
        format: "currency",
        isPrimary: true,
      },
      {
        key: "totalAmount",
        label: "Total Amount (Principal + Interest)",
        format: "currency",
      }
    ],
    content: {
      whatIs:
        "Simple Interest is the foundational concept of interest calculation: interest is calculated ONLY on the principal amount, never on accumulated interest. Using the formula SI = P × R × T / 100, the total amount owed/earned is Principal + Simple Interest. On ₹1 lakh at 10% for 3 years, SI = ₹30,000, so total = ₹1.30 lakh. Simple interest grows linearly—each year adds the same amount of interest. While simple interest is less common in modern banking and FDs (which use compound), it's still used in Government Securities, bonds, Treasury bills, and some short-term loans. In personal finance context, simple interest generally favors lenders (lower repayment on loans) but disadvantages savers (lower returns vs compound). Understanding simple interest is important for comparing short-term investments (where simple and compound are similar) and recognizing why compound interest is almost always better for savers. This calculator helps you understand what you'd earn under simple interest, then compare it to compound interest options for the same investment.",
      faqs: [
        {
          question:
            "When is simple interest used?",
          answer:
            "Short-term loans (under 1 year), bonds, Government Securities, Treasury bills. Most savings accounts and FDs use compound interest (better for savers, worse for borrowers).",
        },
        {
          question:
            "Is simple interest better than compound for savers?",
          answer:
            "No. Compound interest always grows faster (interest earns interest). Simple interest grows linearly, compound exponentially. Avoid simple interest schemes; prefer compound.",
        },
        {
          question:
            "Example: ₹1L loan at 10% p.a. simple interest for 3 years?",
          answer:
            "SI = ₹1L × 10% × 3 = ₹30K. Total to repay = ₹1.3L. At compound: ₹1.33L. Difference = ₹3K, small for short periods but compounds over time.",
        },
        {
          question:
            "Can I use simple interest for investment planning?",
          answer:
            "Not recommended. Real investments compound. If someone offers 'simple interest' savings, question reliability. Modern investing is all compound.",
        },
        {
          question:
            "How much more does compound beat simple interest?",
          answer:
            "Over 5 years at 8%: compound = ₹1.47L, simple = ₹1.4L (difference = ₹7K). Over 20 years: compound = ₹4.66L, simple = ₹2.6L (difference = ₹2.06L!). Time amplifies the difference.",
        }
      ],
      sections: [
        {
          title: "When Simple Interest is Still Used",
          body: "Government Securities (bonds), Treasury bills (short-term), some short-duration debt funds, and personal loans between friends often use simple interest. Banks also use simple interest for calculating interest on negative balances (overdrafts). In modern banking, most deposits use compound, but understanding simple interest helps you evaluate older bonds or non-bank borrowing scenarios.",
        },
        {
          title: "Simple vs Compound: The Comparison",
          body: "Simple interest grows linearly (straight line on a graph). Compound interest grows exponentially (curved, accelerating). The difference is negligible for 1-2 years (₹5-10K) but massive over 20+ years (₹1-2L+). This is why borrowers prefer simple interest loans, but savers prefer compound interest investments.",
        },
        {
          title: "Simple Interest Formula Explained",
          body: "SI = Principal × Rate × Time / 100. For ₹100K at 8% for 5 years: SI = 100000 × 8 × 5 / 100 = ₹40,000. Total amount = ₹100K + ₹40K = ₹140K. Every year adds exactly ₹8,000 interest, regardless of growing principal.",
        },
        {
          title: "Government Securities and Simple Interest",
          body: "Many Indian Government Securities (bonds, development bonds) use simple interest. For example, if you buy a ₹1L bond at 6% simple interest for 7 years, you get ₹42,000 interest over the period. Compare with equivalent FD (compound) at 6.5%: you'd get ₹52,000+ interest. This is why bonds attract savers seeking liquidity, not maximum returns.",
        },
        {
          title: "Why Banks Prefer Compound for Deposits",
          body: "Banks offer compound interest on deposits to attract investors (better returns than simple). But on loans, banks typically compound too (more interest for them). As an individual lender, simple interest loans are rarer now but valuable for understanding fundamentals of credit and interest.",
        },
      ],
      relatedSlugs: [
        "savings/compound-interest-calculator",
        "savings/fd-calculator",
      ],
    },
  }),

  // 6. PPF Calculator Detailed
  createCompoundInterestTool({
    slug: "ppf-calculator-detailed",
    category: "savings",
    title: "PPF Detailed Calculator with Annual Deposits",
    shortTitle: "PPF Annual Deposits",
    description: "Tax-free wealth: ₹1.5L annual investment, guaranteed return",
    keywords: [
      "ppf detailed",
      "ppf year by year",
      "ppf calculator",
      "public provident fund",
    ],
    defaultAmount: 150000,
    maxAmount: 1500000,
    defaultRate: 7.1,
    defaultYears: 15,
    maxYears: 50,
    compoundingFrequency: 1,
    whatIs:
      "Public Provident Fund (PPF) is India's flagship government-backed savings scheme, trusted by millions for tax-efficient wealth creation. You can invest up to ₹1.5 lakh annually (₹1 lakh in 2023-24, increased to ₹1.5 lakh in recent years—verify current limit). The entire investment qualifies for Section 80C tax deduction, saving ₹30-45K in annual taxes (at 30% slab). PPF offers guaranteed 7.1% p.a. annual returns, completely tax-free—both the interest earned and final maturity are untaxed. This triple benefit (immediate tax deduction + guaranteed return + zero tax on growth) makes PPF unbeatable for conservative savers. 15-year lock-in ensures disciplined wealth building; you can extend for 5-year blocks after maturity. Partial withdrawal (50% after 7th year) and loans (up to 25%) are available for emergencies. Many salaried individuals and business owners max out PPF (₹1.5L/year) and achieve retirement corpus targets: ₹1.5L/year for 25 years = ₹37.5L contributed, grows to ~₹85-95L at 7.1%. PPF is accessible to anyone—salaried, self-employed, homemakers—making it India's most inclusive wealth-building instrument.",
    sections: [
      {
        title: "Who Should Invest Maximum in PPF?",
        body: "Anyone in 20-40% tax slab should max out PPF annually. Salaried employees earning ₹10L+, business owners, and professionals with high tax liability benefit most. Even if you can't max it, contributing ₹50-75K/year still builds significant wealth. Government employees, homemakers with investment allocations, and retirees looking for tax-efficient savings should prioritize PPF.",
      },
      {
        title: "Section 80C Tax Benefits Explained",
        body: "Section 80C allows ₹1.5L deduction from taxable income for PPF investments. At 30% tax slab, ₹1.5L contribution saves ₹45K in taxes annually. Over 15 years, cumulative tax saving = ₹6.75L. This is NOT a rebate; it's deduction from income. When combined with 7.1% guaranteed return, PPF delivers unmatched tax-efficiency compared to FD (interest fully taxed) or regular savings.",
      },
      {
        title: "Partial Withdrawal & Loan Features",
        body: "From 7th year onwards, withdraw 50% of balance annually for education, marriage, or medical needs. No penalty; interest continues on remaining balance. Additionally, borrow up to 25% of balance (at lower interest) for emergencies. These features provide liquidity while maintaining forced savings discipline. Use withdrawal/loan strategically, not for frivolous needs.",
      },
      {
        title: "Post-Maturity Extension Strategy",
        body: "After 15 years, you can extend in 5-year blocks indefinitely. Most investors extend repeatedly since 7.1% (PPF extension rate) beats FD rates often. You can withdraw full balance at 15 years or extend and continue earning tax-free compounding. Extension is flexible: withdraw anytime after maturity without lock-in restrictions.",
      },
      {
        title: "Why PPF Beats FD for Tax-Conscious Investors",
        body: "FD: 6.5% nominal, but interest taxed (say 30%), = 4.5% after-tax. PPF: 7.1% with zero tax = 7.1% actual return. Over 20 years, PPF returns 58% MORE than FD due to tax compounding. Additionally, PPF deduction saves ₹45K/year in taxes, which can be reinvested. This dual benefit makes PPF mathematically superior for high earners.",
      },
    ],
    faqs: [
      {
        question:
          "Can I withdraw PPF before 15 years?",
        answer:
          "Partial: yes, 50% of balance from 7th year. Full withdrawal only at 15 years. Can extend for 5-year blocks thereafter. Designed for long-term wealth building.",
      },
      {
        question:
          "Should I max out ₹1.5L PPF every year?",
        answer:
          "Absolutely, if you can afford it. Tax benefit (₹1.5L deduction) + guaranteed 7.1% return + tax-free returns = unbeatable for salaried/business income earners.",
      },
      {
        question:
          "What's the tax benefit of PPF investment?",
        answer:
          "Direct: ₹1.5L deduction saves ₹45K tax annually (at 30% slab) = ₹45K × 15 years = ₹6.75L cumulative tax savings. Plus tax-free returns and maturity.",
      },
      {
        question:
          "Is PPF better than NPS or mutual funds?",
        answer:
          "PPF: 7.1% guaranteed, safest, limited withdrawals. NPS: variable return (~8-11%), tax-deductible, more flexible. MF: 10-12% potential, fully taxable gains. Best: combine all three.",
      },
      {
        question:
          "What if I don't have ₹1.5L/year to invest?",
        answer:
          "Invest what you can. Even ₹50K/year PPF grows to ₹15-20L in 25 years. Consistency matters more than amount. Start small, increase when income grows.",
      }
    ],
    relatedSlugs: [
      "investment/ppf-calculator",
      "savings/nsc-calculator",
      "investment/elss-calculator",
    ],
  }),

  // 7. SSY Calculator Detailed
  createCompoundInterestTool({
    slug: "ssy-calculator-detailed",
    category: "savings",
    title: "Sukanya Samriddhi Yojana - Annual Deposits",
    shortTitle: "SSY Annual Deposits",
    description: "Daughters' wealth: 8.2% tax-free returns, 21 years",
    keywords: [
      "ssy detailed",
      "sukanya samriddhi",
      "girl child savings",
      "education fund",
    ],
    defaultAmount: 50000,
    maxAmount: 1500000,
    defaultRate: 8.2,
    defaultYears: 21,
    maxYears: 30,
    compoundingFrequency: 1,
    whatIs:
      "Sukanya Samriddhi Yojana (SSY) is India's government-backed savings scheme exclusively for girl children (below 10 years), delivering India's highest guaranteed returns at 8.2% p.a.—0.8% higher than PPF. Parents or guardians invest up to ₹1.5 lakh annually per daughter, fully deductible under Section 80C, with all interest and maturity completely tax-free. After 21 years, investment of ₹1.5L/year (₹31.5L total) compounds to ₹85-95 lakh—more than enough to cover education (₹20-30L) and marriage expenses (₹50-60L). SSY was specifically designed to address gender inequality and encourage parents to invest in girls' futures. Unlike typical savings schemes, SSY has provisions for education withdrawal (50% after 18 years) and marriage withdrawal (full balance at 21). The scheme is operated by post offices and banks across India, with government guarantee ensuring absolute safety. Many parents open SSY accounts the moment a daughter is born, leveraging compounding for 21 years. Compared to PPF (7.1%, 15-year lock, unisex), SSY offers higher returns, longer tenure, and female-specific benefits—making it the preferred choice for daughters.",
    sections: [
      {
        title: "Why SSY is Perfect for Daughters",
        body: "SSY targets girls' education and marriage, societal necessities that require large capital. ₹1.5L/year for 21 years compounds to ₹85-95L with zero tax—sufficient for top university education (₹20-30L) and marriage (₹50-60L). Parents start young (daughter age 0-10), ensuring maximum compounding. Additionally, SSY signals to families: invest in girls' futures; government supports and guarantees returns.",
      },
      {
        title: "Flexibility: Education and Marriage Withdrawals",
        body: "At age 18, withdraw 50% of balance for education without closing account. Continue earning 8.2% on remaining 50%. At age 21, withdraw full balance for marriage or higher education. Partial withdrawal before 18 not allowed (intentional discipline). This structure ensures funds aren't depleted frivolously but available for major life events.",
      },
      {
        title: "Section 80C Benefits for Multiple Daughters",
        body: "Each daughter gets separate SSY account. For two daughters, invest ₹1.5L in each (₹3L total), claiming ₹1.5L deduction per daughter. Larger families can maximize Section 80C across daughters. Example: ₹1.5L/year SSY for daughter 1 + ₹1.5L/year SSY for daughter 2 = ₹3L invested, ₹3L Section 80C deduction, = ₹90K tax saving (at 30% slab).",
      },
      {
        title: "Comparing SSY vs PPF vs Other Schemes",
        body: "SSY: 8.2%, 21-year lock, girls-only, highest returns. PPF: 7.1%, 15-year lock, anyone, tax-deductible. FD: 6-7%, any tenure, DICGC insured, no special benefits. For daughters, SSY is mathematically best. For general savings, PPF is more flexible (early withdrawal after 7 years). For maximum safety (not returns), FD across multiple banks is preferred.",
      },
      {
        title: "Account Opening and Operation",
        body: "Open SSY account at any post office or bank participating in the scheme. Requires girl's birth certificate and parent/guardian ID. Investment can be monthly, quarterly, or annual (lump-sum). Most families set up auto-transfer on daughter's birthday. Once opened, let compounding work for 21 years without intervention. It's truly 'set and forget' wealth building.",
      },
    ],
    faqs: [
      {
        question:
          "Can I withdraw SSY after 21 years?",
        answer:
          "Yes. Full withdrawal at 21 years. You can withdraw 50% after age 18 (for education/marriage). Designed with flexibility for major life events.",
      },
      {
        question:
          "Should I max ₹1.5L/year SSY for my daughter?",
        answer:
          "If you can, yes. Over 21 years, ₹1.5L/year = ₹31.5L contributed. With 8.2% return, grows to ~₹80-90L. Perfect for education (₹25-30L) + marriage (₹50-60L).",
      },
      {
        question:
          "Is SSY better than PPF for my daughter?",
        answer:
          "SSY: 8.2%, 21-year lock. PPF: 7.1%, 15-year lock. SSY for girls (extra benefit rate). PPF if you need unlocking at 15 years. SSY is superior for girls' savings.",
      },
      {
        question:
          "Can I open SSY for multiple daughters?",
        answer:
          "Yes, one account per girl child. For 2 daughters: invest ₹1.5L in each (₹3L total). Each grows independently, but each eligible for ₹1.5L tax deduction.",
        },
      {
        question:
          "What if daughter marries before 21 years?",
        answer:
          "SSY matures at 21, but you can withdraw 50% after 18 years for marriage. Or continue till 21 for full benefit. Account remains open unless explicitly closed.",
      },
    ],
    relatedSlugs: [
      "investment/ssy-calculator",
      "savings/ppf-calculator",
      "savings/nsc-calculator",
    ],
  }),

  // 8. NSC Calculator Detailed
  createCompoundInterestTool({
    slug: "nsc-calculator-detailed",
    category: "savings",
    title: "NSC with Reinvestment Calculator",
    shortTitle: "NSC Reinvestment",
    description: "Post office savings: 7.7% guaranteed, 5-year tenure",
    keywords: [
      "nsc reinvestment",
      "national savings certificate",
      "post office",
      "renewable scheme",
    ],
    defaultAmount: 100000,
    maxAmount: 10000000,
    defaultRate: 7.7,
    defaultYears: 5,
    maxYears: 20,
    compoundingFrequency: 1,
    whatIs:
      "National Savings Certificate (NSC) is a post office savings scheme offered by India Post, providing 7.7% guaranteed annual returns with a fixed 5-year lock-in period. You can invest any amount from ₹1,000 to ₹9.99 lakh (per person per financial year) at any post office. Unlike FD (which compounds automatically), NSC pays interest annually, but you can reinvest that interest to create additional compounding. Alternatively, interest is credited to your savings account. NSC is designed for disciplined savers who want government-backed safety and higher returns than FD, without stock market risk. The scheme is unique because it doubles your investment in approximately ~10 years due to the 7.7% rate—hence the motto 'double your money in 10 years.' At maturity (5 years), you can reinvest the entire amount into a new NSC, extending the lock-in for another 5 years, creating a perpetual wealth-building cycle. Many retirees and conservative investors prefer NSC over FD because the post office has direct government backing and NSC historically maintains higher rates than most banks. NSC interest is fully taxable but can be reinvested to defer tax liability by claiming Form 15G/15H.",
    sections: [
      {
        title: "Who Should Invest in NSC?",
        body: "Conservative savers seeking guaranteed returns with zero market risk, retirees building stable income, parents funding children's education, and individuals near retirement wanting capital protection. NSC is especially popular among senior citizens and individuals in high-income brackets who want tax-deferred compounding through reinvestment.",
      },
      {
        title: "NSC Doubling Feature: 7.7% Advantage",
        body: "At 7.7% return, your investment doubles in approximately 9.3 years (using Rule of 72: 72/7.7 ≈ 9.3). This 'doubling' is NSC's main selling point. ₹5L becomes ₹10L in ~10 years; ₹10L becomes ₹20L in ~20 years. Reinvesting at maturity extends this doubling. This makes NSC excellent for long-term (20-30 year) wealth building without active management.",
      },
      {
        title: "Reinvestment Strategy for Maximum Compounding",
        body: "At 5-year maturity, reinvest the entire amount into new NSC. Don't withdraw and spend. This extends lock-in but maximizes compounding: ₹1L → ₹1.44L (5 yrs) → ₹2.05L (10 yrs) → ₹2.94L (15 yrs). If you need income, reinvest principal and use interest payout only for expenses. This balances growth with income needs.",
      },
      {
        title: "Interest Taxation & Form 15G Benefits",
        body: "NSC interest is fully taxable annually. If annual interest exceeds ₹10K, banks deduct TDS at 20-30%. To avoid TDS (if income below taxable limit), submit Form 15G. For senior citizens, up to ₹50K interest is deductible under Section 80TTB. Tax-wise, reinvesting interest compounds your wealth more than taking it out (since taken interest is immediately taxed).",
      },
      {
        title: "NSC vs FD vs PPF: Which to Choose",
        body: "NSC: 7.7%, 5-year lock, post office, reinvestment-friendly. FD: 6-7%, any tenure, bank, flexible withdrawal. PPF: 7.1%, 15-year lock, ₹1.5L/year limit, Section 80C benefit. For maximum returns with lock-in comfort, NSC wins. For flexibility, FD wins. For tax-efficiency, PPF wins. Ideally, combine all three: PPF for tax-deduction limit, NSC for reinvestment discipline, FD for remaining corpus.",
      },
    ],
    faqs: [
      {
        question:
          "Should I reinvest NSC at maturity or withdraw?",
        answer:
          "Reinvest if you can lock capital again. Reinvestment extends compounding, roughly doubling corpus every 10 years. Withdraw only if you need funds for major goal.",
      },
      {
        question:
          "Can I reinvest NSC multiple times?",
        answer:
          "Yes, unlimited. NSC is designed for continuous reinvestment. Each 5-year cycle earns 7.7%, compounding cumulatively. No limit on renewals.",
      },
      {
        question:
          "Is NSC interest reinvestment taxed?",
        answer:
          "At each maturity, interest is taxable as income. Reinvestment itself is not a taxable event, but compound interest generation is taxed. Plan for tax liability.",
      },
      {
        question:
          "NSC vs FD: which is better for long-term?",
        answer:
          "NSC: 7.7%, locked 5 years, government-backed. FD: 6-7%, liquid anytime, bank-backed. NSC for forced savings discipline; FD for flexibility. Over 20 years, NSC wins due to higher rate.",
      },
      {
        question:
          "Can I buy NSC from bank or only post office?",
        answer:
          "Post office primarily. Some banks (SBI, Bank of India) sell NSC. Max ₹9.99L per person per financial year. Minimum ₹1,000.",
        },
    ],
    relatedSlugs: [
      "savings/nsc-calculator",
      "savings/fd-calculator",
      "savings/ppf-calculator",
    ],
  }),

  // 9. KVP Maturity
  createEMITool({
    slug: "kvp-maturity",
    category: "savings",
    title: "KVP Maturity Calculator - Doubling Feature",
    shortTitle: "KVP Maturity",
    description:
      "Calculate Kisan Vikas Patra maturity with automatic doubling",
    keywords: [
      "kvp maturity",
      "kisan vikas patra",
      "doubling",
      "rural savings",
      "automatic doubling",
    ],
    inputs: [
      {
        type: "number",
        key: "investment",
        label: "KVP Investment Amount",
        prefix: "₹",
        min: 1000,
        max: 10000000,
        step: 1000,
        default: 100000,
      },
      {
        type: "number",
        key: "years",
        label: "Time Period",
        suffix: "years",
        min: 5,
        max: 30,
        step: 1,
        default: 10,
      }
    ],
    calculate(v) {
      const amount = v.investment as number;
      const years = v.years as number;
      const rate = 0.075; // 7.5%

      const maturityAmount = amount * Math.pow(1 + rate, years);

      // Doubling time
      const doublingTime = Math.log(2) / Math.log(1 + rate);
      const doublesAchieved = Math.floor(years / doublingTime);
      const doubledValue =
        amount * Math.pow(2, doublesAchieved);

      return {
        investment: amount,
        years: years,
        maturityAmount: Math.round(maturityAmount),
        doublesAchieved: doublesAchieved,
        doubledValue: Math.round(doubledValue),
        doublingTimeNeeded: Math.round(doublingTime * 10) / 10,
      };
    },
    resultFields: [
      {
        key: "maturityAmount",
        label: "Maturity Amount",
        format: "currency",
        isPrimary: true,
      },
      {
        key: "doubledValue",
        label: "Doubled Value (Confirmed Doubles)",
        format: "currency",
      },
      {
        key: "doublingTimeNeeded",
        label: "Time to Double Once",
        format: "years",
      }
    ],
    content: {
      whatIs:
        "Kisan Vikas Patra (KVP) is a government savings scheme operated through post offices, offering a unique feature: automatic doubling of your investment at a fixed maturity period (~9.5 years at 7.5% return). The concept is deceptively simple: invest ₹1 lakh today, and after ~9.5 years of compounding at 7.5%, it automatically becomes ₹2 lakh. No active reinvestment, no decision-making—purely automatic. KVP was originally designed for farmers (hence the name 'Kisan' = farmer), but it's available to anyone: salaried employees, retirees, homemakers, and businesses. You can invest from ₹1,000 to ₹9.99 lakh per person per financial year. The 'doubling' concept makes KVP psychologically appealing and easy to understand: invest once, forget, and get double back. While 7.5% is not exceptionally high compared to FD (6-7%) or NSC (7.7%), the simplicity and government backing attract conservative investors. After the doubling period, you can reinvest for continued growth or withdraw. Unlike PPF (₹1.5L/year limit), NSC (Section 80C), KVP has no special tax benefits but offers a fixed, predictable doubling benefit without tax complications.",
      faqs: [
        {
          question:
            "How does KVP automatic doubling work?",
          answer:
            "Not 'magic'—just compounding at 7.5%. Mathematically, ₹1L grows to ₹2L in ~9.5 years at 7.5%. Called 'doubling' because that's the key milestone. After doubling, continued growth follows same rate.",
        },
        {
          question:
            "What if I need money before doubling?",
          answer:
            "You can withdraw anytime (no lock-in technically). But early withdrawal: pro-rata interest only, no doubling benefit. Keep invested 9.5+ years for full benefit.",
        },
        {
          question:
            "Is KVP better than PPF?",
          answer:
            "KVP: 7.5%, doubles in 9.5 years. PPF: 7.1%, 15-year lock. KVP faster doubling, but less flexible. PPF tax-deductible, KVP not. Both safe; choose based on lock-in tolerance.",
        },
        {
          question:
            "Can I buy unlimited KVP?",
          answer:
            "Individual: max ₹9.99L per financial year. HUF/Company: higher limits. No lifetime cap. If you have ₹20L, buy ₹9.99L this year, ₹10L next year.",
        },
        {
          question:
            "What after KVP maturity/doubling?",
          answer:
            "Redeem (get ₹2L from ₹1L investment) or reinvest new KVP. Best: reinvest for continued compounding. Each KVP cycle is independent.",
        }
      ],
      sections: [
        {
          title: "The Psychology of Doubling in KVP",
          body: "KVP's main attraction is the 'doubling' concept—psychologically powerful for savers. Rather than saying '7.5% returns in 9.5 years,' the scheme says 'your money doubles automatically.' This simplicity appeals to individuals averse to calculations. Many savers use KVP purely for this predictability: invest once, get double back, no surprises or tax complications.",
        },
        {
          title: "KVP Doubling Time: Rule of 72",
          body: "At 7.5% return, money doubles in 72/7.5 ≈ 9.6 years (Rule of 72). This is not magic—just mathematical compounding. ₹1L → ₹1.5L in 5 years → ₹2L in 9.6 years → ₹4L in 19 years. The longer you leave money invested, the more doublings you achieve.",
        },
        {
          title: "KVP vs FD vs NSC for Doubling",
          body: "NSC: 7.7% (doubles in ~9.3 years, slightly faster). FD: 6.5% (doubles in ~11 years). KVP: 7.5% (doubles in ~9.6 years). NSC is marginally better, but KVP's simplicity appeals to farmers and rural savers. For urban investors, NSC or FD might offer better returns, but KVP is perfectly fine.",
        },
        {
          title: "Reinvestment: Creating Multiple Doublings",
          body: "After 9.6 years, reinvest the doubled amount (₹2L from ₹1L) into new KVP. By year 19, you have ₹4L. By year 29, ₹8L. The compounding accelerates exponentially. Most investors reinvest repeatedly until retirement, creating significant wealth from a single initial investment through multiple doubling cycles.",
        },
        {
          title: "KVP as Emergency Fund Substitute",
          body: "While PPF and NSC offer earlier withdrawal (PPF: 50% after 7 years; NSC: early redemption with penalty), KVP's lock-in is strict. If liquidity is critical, avoid KVP. Use KVP only for capital you won't need for 10+ years. This strictness ensures discipline but sacrifices flexibility.",
        },
      ],
      relatedSlugs: [
        "savings/ppf-calculator",
        "savings/nsc-calculator",
        "savings/fd-calculator",
      ],
    },
  }),

  // 10. SCSS Returns
  createCompoundInterestTool({
    slug: "scss-returns",
    category: "savings",
    title: "SCSS Returns - Senior Citizens Scheme",
    shortTitle: "SCSS Returns",
    description: "Highest return for seniors 60+: 8.2%, safe, income-rich",
    keywords: [
      "scss",
      "senior citizens savings scheme",
      "elderly savings",
      "post office",
      "senior returns",
    ],
    defaultAmount: 500000,
    maxAmount: 30000000,
    defaultRate: 8.2,
    defaultYears: 5,
    maxYears: 10,
    compoundingFrequency: 4,
    whatIs:
      "Senior Citizens Savings Scheme (SCSS) is India's highest-returning government savings scheme exclusively for citizens aged 60+ (or 55+ if voluntarily retired from government service). SCSS offers a guaranteed 8.2% p.a. return with quarterly interest payouts, making it perfect for retirees needing regular monthly/quarterly income. Minimum investment is ₹1 lakh, maximum ₹30 lakh per person. The initial tenure is 5 years, extendable for 3-year blocks after maturity (max 11 years total). Unlike cumulative schemes like PPF where interest reinvests, SCSS pays interest directly to your bank account quarterly—providing actual cash flow for living expenses. This makes SCSS ideal for retirees: ₹30L in SCSS generates ₹2.46L annually (₹61.5K quarterly or ~₹20.5K monthly), supplementing pension and other income. The scheme is post office-based, government-backed, and completely safe—more secure than bank FDs. Many retired couples combine their savings in SCSS, achieving income security without equity or bond market exposure. Interest is fully taxable, but most retirees with limited post-retirement income fall below taxable limits, making SCSS tax-efficient for their life stage.",
    sections: [
      {
        title: "Why SCSS is Ideal for Retirees",
        body: "Retirees need safety (no stock market volatility), income (quarterly payouts), and certainty (fixed rate locked in). SCSS delivers all three: 8.2% guaranteed, quarterly cash flow, government-backed safety. ₹30L corpus generates ₹2.46L annual income (₹20.5K/month)—often sufficient to supplement pension and maintain lifestyle.",
      },
      {
        title: "Calculating Monthly Income from SCSS",
        body: "At 8.2% p.a., ₹30L generates ₹2.46L yearly. Quarterly payout = ₹61.5K. Monthly equivalent = ₹20.5K. For couples: ₹30L each (₹60L total) = ₹41K monthly combined income. This, combined with pension, covers most household expenses for average retirees.",
      },
      {
        title: "Post-Maturity Extension and Flexibility",
        body: "After 5 years, extend for 3-year blocks (up to 11 years total). Rate remains locked at purchase rate (8.2% currently). No need to reinvest; just extend. This provides decades of predictable income without managing multiple investments. After final extension (11 years), you can withdraw full corpus and reinvest if desired.",
      },
      {
        title: "Tax Implications for Retirees",
        body: "SCSS interest is fully taxable. However, many retirees have limited post-retirement income (pension + SCSS interest only), falling below ₹5L taxable limit. Result: no tax liability despite interest income. Even for higher-income retirees, SCSS is more tax-efficient than equity investments. File Form 60 or use senior citizen rebate under Section 87A if applicable.",
      },
      {
        title: "SCSS as Core Retirement Portfolio Component",
        body: "Ideal allocation: 50% SCSS (for income + safety), 30% FD/NSC (for liquidity), 20% equity/ELSS (for inflation protection). This provides stable monthly income (SCSS), flexibility (FD/NSC), and growth (equity). Pure SCSS at 8.2% doesn't beat inflation (5%); hence equity allocation is important for long-term retiree wealth preservation.",
      },
    ],
    faqs: [
      {
        question:
          "Who is eligible for SCSS?",
        answer:
          "Indian citizens aged 60+ years. Retired government employees from age 55+ (with superannuation proof). Joint account with spouse (both must be 60+). No income/education requirement.",
      },
      {
        question:
          "How much income does SCSS generate?",
        answer:
          "At 8.2%, a ₹30L investment generates ₹6L interest over 5 years (₹1L/year or ₹25K/quarter). Max ₹30L per person. Two seniors: ₹60L corpus = ₹12L income over 5 years.",
      },
      {
        question:
          "Is SCSS interest taxable?",
        answer:
          "Yes, fully taxable as income. But for most seniors with limited income, falls below taxable limit (₹5L+). Use Form 60 to minimize TDS or claim rebate under Section 87A.",
      },
      {
        question:
          "Can I extend SCSS beyond 5 years?",
        answer:
          "Yes. After 5 years, extend for additional 3-year periods (max 2 extensions total = 11 years max). Rate remains 8.2% during extensions (locked at purchase rate).",
      },
      {
        question:
          "Is SCSS safe?",
        answer:
          "Absolutely. Government-backed post office scheme. Capital fully protected; returns guaranteed. DICGC insurance also applies. Among safest investments for seniors.",
        },
    ],
    relatedSlugs: [
      "savings/post-office-mis",
      "savings/fd-calculator",
      "retirement/pension-calculator",
    ],
  }),

  // 11. Tax-Saver FD
  createCompoundInterestTool({
    slug: "td-calculator",
    category: "savings",
    title: "Tax-Saver FD Calculator",
    shortTitle: "Tax-Saver FD",
    description: "Section 80C deduction, 5-year lock, guaranteed returns",
    keywords: [
      "tax-saver fd",
      "tax saving fd",
      "5 year fd",
      "section 80c",
      "tax deduction",
    ],
    defaultAmount: 150000,
    maxAmount: 1000000,
    defaultRate: 5.9,
    defaultYears: 5,
    maxYears: 10,
    compoundingFrequency: 1,
    whatIs:
      "Tax-Saver FD is a government-approved bank FD product with a mandatory 5-year lock-in, offering two tax benefits: (1) Full investment amount qualifies for Section 80C deduction (reducing taxable income), and (2) Interest earned is taxed post-maturity rather than annually. Guaranteed returns range from 5.5-6.5% p.a., lower than regular FDs but acceptable given tax benefits. Unlike mutual funds (ELSS: 3-year lock, volatile returns but higher growth potential), Tax-Saver FDs offer certainty: you know exact maturity amount. On ₹1.5L investment at 6% for 5 years: saves ₹45K in immediate tax (at 30% slab) + earns ~₹48K interest, net benefit ~₹25-30K. Not as high-returning as equities or ELSS, but provides guaranteed growth with tax-efficient deduction. Many conservative investors and those averse to market volatility prefer Tax-Saver FDs for their Section 80C allocation, especially if they've already maxed out PPF or NPS options.",
    sections: [
      {
        title: "Who Benefits Most from Tax-Saver FD?",
        body: "High-income earners (₹20L+) in 30-42% tax brackets, conservative investors avoiding equity risk, those who've maxed PPF/NPS, and business owners with variable income. If you're risk-averse and need Section 80C deduction, Tax-Saver FD is reliable. Young professionals can use it for disciplined wealth-building with tax efficiency.",
      },
      {
        title: "Section 80C Deduction vs Interest Taxation",
        body: "Deduction: ₹1.5L FD saves ₹45K tax immediately (at 30% slab). Interest: On ₹1.5L at 6%, earn ₹48K interest over 5 years. Interest is fully taxable at maturity (not annually). Net tax benefit: ₹45K deduction minus tax on ₹48K interest = ₹20-25K actual saving depending on applicable slab.",
      },
      {
        title: "Comparing Tax-Saver FD vs ELSS Mutual Fund",
        body: "ELSS: 3-year lock, potential 12-15% returns, capital gains tax (0% if held 1+ year from 2024). Tax-Saver FD: 5-year lock, guaranteed 5.5-6.5%, interest fully taxed. For growth: ELSS wins. For certainty: Tax-Saver FD wins. Many investors do both: ELSS for growth, Tax-Saver FD for stability.",
      },
      {
        title: "Lock-in Implications and Withdrawal Penalties",
        body: "Tax-Saver FD is locked for 5 years. Early withdrawal before completion: penalties are steep (no interest if <1 year, interest minus penalty if 1-5 years). This strictness forces discipline but sacrifices flexibility. If you might need funds within 5 years, avoid Tax-Saver FD; use regular FD or other liquid instruments.",
      },
      {
        title: "Maximizing Section 80C Across Investments",
        body: "Section 80C limit is ₹1.5L/year. Allocation strategy: PPF ₹1.5L (highest priority: tax-free returns) → if need more, ELSS ₹1.5L (growth potential) → if still more, Tax-Saver FD ₹1.5L (guaranteed). Don't use Tax-Saver FD if you haven't maxed PPF or ELSS first. Prioritize by return potential and flexibility.",
      },
    ],
    faqs: [
      {
        question:
          "What's the tax benefit of tax-saver FD?",
        answer:
          "₹1.5L investment deduction: saves ₹45K tax (at 30% slab). At 5.9% return, earn ~₹48K interest. Net benefit: tax saving minus interest taxes = potentially ₹20-30K depending on slab.",
      },
      {
        question:
          "Should I choose tax-saver FD or ELSS?",
        answer:
          "ELSS: 3-year lock-in, high growth (12-15% potential), higher risk. Tax-saver FD: 5-year lock-in, guaranteed 5-6.5%, no risk. ELSS if you can handle volatility; FD if conservative.",
      },
      {
        question:
          "Can I withdraw tax-saver FD before 5 years?",
        answer:
          "Yes, but penalty: no interest if <1 year, interest-minus-1% if 1-5 years. Not recommended. Designed for full 5-year lock-in.",
      },
      {
        question:
          "Is tax-saver FD return after-tax?",
        answer:
          "No, stated return is pre-tax. If FD earns 5.9%, interest is fully taxable. After 30% tax: net return ~4.1%. Still reasonable compared to savings account (2-4% pre-tax = 1.4-2.8% after tax).",
      },
      {
        question:
          "Can I open multiple tax-saver FDs?",
        answer:
          "Yes, but combined Section 80C deduction limit is ₹1.5L across all schemes (PPF, NPS, ELSS, tax-saver FD, etc.). Can't exceed ₹1.5L total.",
        },
    ],
    relatedSlugs: [
      "investment/elss-calculator",
      "savings/fd-calculator",
      "savings/ppf-calculator",
      "tax/section-80c-calculator",
    ],
  }),

  // 12. Mahila Samman Savings Certificate
  createCompoundInterestTool({
    slug: "mahila-samman-savings",
    category: "savings",
    title: "Mahila Samman Savings Certificate Calculator",
    shortTitle: "Mahila Samman",
    description: "Women's savings: 7.75% return, 2-year tenure, no lock-in",
    keywords: [
      "mahila samman",
      "women savings",
      "women scheme",
      "government scheme for women",
    ],
    defaultAmount: 100000,
    maxAmount: 2000000,
    defaultRate: 7.75,
    defaultYears: 2,
    maxYears: 10,
    compoundingFrequency: 1,
    whatIs:
      "Mahila Samman Savings Certificate (MSSC) is a government-backed savings scheme exclusively for Indian women (18+ years), offering 7.75% p.a. guaranteed returns with a fixed 2-year tenure. Launched in October 2023, MSSC represents the government's commitment to financial inclusion and women's empowerment. You can invest from ₹1,000 to ₹2 lakh per woman per financial year. Unlike many savings schemes with strict lock-ins, MSSC allows NO withdrawal restrictions post-opening—you can withdraw anytime without penalty, though early withdrawal before 1 year attracts reduced interest. The scheme emphasizes accessibility: minimal documentation, available at post offices nationwide, and straightforward operations. ₹1 lakh in MSSC at 7.75% for 2 years generates ₹15,970 interest, resulting in ₹1,15,970 maturity. This makes MSSC attractive compared to regular FD (6-7%), savings account (3-4%), or bonds. The 2-year tenure is shorter than FD/NSC (5 years), making it ideal for women saving for short-term goals: starting emergency fund, children's education, home decoration, or wedding expenses. Many housewives and working women use MSSC as a parallel to their monthly savings, building financial independence through accessible, women-focused government scheme.",
    sections: [
      {
        title: "Why MSSC Empowers Women Financially?",
        body: "MSSC specifically targets women's financial inclusion, recognizing that women (housewives, single mothers, working professionals) often have separate savings goals. The scheme says: 'Your savings matter; government guarantees and supports them.' Access to guaranteed-return investments improves women's economic confidence and decision-making autonomy. Many women prefer MSSC over bank FDs for psychological empowerment.",
      },
      {
        title: "Flexible Withdrawal vs Locked Alternatives",
        body: "MSSC unique feature: no lock-in post-opening. Withdraw anytime without government penalty. Early withdrawal (before 1 year): 0.5% reduction in interest. This flexibility beats NSC (5-year lock), KVP (9.5-year doubling period), and PPF (7-year partial withdrawal). Ideal if life circumstances change unexpectedly.",
      },
      {
        title: "MSSC vs FD vs Savings Account",
        body: "MSSC: 7.75%, 2-year, women-only, no-lock-in. FD: 6-7%, any tenure, any gender, flexible maturity. Savings Account: 3-4%, fully liquid, easier ATM access. For women seeking balance between returns and access, MSSC wins. Returns exceed FD; access better than NSC. Recommended for women's dedicated savings.",
      },
      {
        title: "How Housewives Can Use MSSC",
        body: "Housewives managing household budget: dedicate monthly surplus to MSSC. ₹5K/month → ₹2L in 40 months → ₹2.31L at 7.75% maturity. This builds personal financial corpus independent of spouse, improving financial negotiation position and security in marriage.",
      },
      {
        title: "MSSC for Education and Emergency Fund",
        body: "Open MSSC for children's education or home emergency fund. 2-year maturity aligns with intermediate education milestones (school transition, college entrance). No lock-in means if emergency (medical, family crisis) arises, withdraw without penalties. Unlike strict-lock schemes, MSSC balances return with accessibility for women's varied life situations.",
      },
    ],
    faqs: [
      {
        question:
          "Who is eligible for Mahila Samman Savings?",
        answer:
          "Indian women citizens aged 18+ years. Can open for daughters (minor females) through guardians. No upper age limit. Joint account with other women also allowed.",
      },
      {
        question:
          "How much can I invest in Mahila Samman?",
        answer:
          "Minimum ₹1,000, maximum ₹2L per woman per year. For multiple women: each can invest separately. No lifetime cap; can reinvest at maturity.",
      },
      {
        question:
          "Is Mahila Samman returns better than FD?",
        answer:
          "MSSC: 7.75%. FD: 6-7%. MSSC is 0.75-1.75% higher. Plus 2-year tenure is shorter than typical 5-year FDs. Good for women's savings.",
      },
      {
        question:
          "Can I withdraw Mahila Samman before 2 years?",
        answer:
          "No pre-maturity withdrawal; strict 2-year lock-in. Full withdrawal at 2 years. If you need liquidity, this scheme is not ideal.",
      },
      {
        question:
          "Is Mahila Samman interest taxed?",
        answer:
          "Yes, fully taxable as income. At 7.75%, interest earned ~₹15.5K on ₹1L/2yr. Taxable as per income slab. Can use TDS Form 15G.",
        },
    ],
    relatedSlugs: [
      "savings/fd-calculator",
      "savings/ppf-calculator",
      "savings/nsc-calculator",
    ],
  }),

  // 13. FD vs RD Comparison
  createEMITool({
    slug: "fd-vs-rd-comparison",
    category: "savings",
    title: "FD vs RD Comparison Calculator",
    shortTitle: "FD vs RD",
    description: "Compare final maturity between FD and RD investments",
    keywords: [
      "fd vs rd",
      "fd comparison",
      "rd comparison",
      "savings comparison",
    ],
    inputs: [
      {
        type: "number",
        key: "totalAmount",
        label: "Total Amount to Invest",
        prefix: "₹",
        min: 100000,
        max: 1000000,
        step: 100000,
        default: 500000,
      },
      {
        type: "number",
        key: "rate",
        label: "Interest Rate",
        suffix: "% p.a.",
        min: 1,
        max: 15,
        step: 0.1,
        default: 6,
      },
      {
        type: "number",
        key: "years",
        label: "Investment Period",
        suffix: "years",
        min: 1,
        max: 20,
        step: 1,
        default: 5,
      }
    ],
    calculate(v) {
      const total = v.totalAmount as number;
      const rate = (v.rate as number) / 100;
      const years = v.years as number;

      // FD: Lumpsum invested
      const fdMaturity = total * Math.pow(1 + rate / 4, 4 * years);

      // RD: Monthly deposits = total / (years * 12)
      const monthlyRD = total / (years * 12);
      const quarterlyRate = rate / 4;
      let rdMaturity = 0;

      for (let m = 1; m <= years * 12; m++) {
        const quartersRemaining = (years * 12 - m) / 3;
        rdMaturity +=
          monthlyRD *
          Math.pow(1 + quarterlyRate, quartersRemaining);
      }

      const fdInterest = fdMaturity - total;
      const rdInterest = rdMaturity - total;
      const fdBenefit = fdMaturity - rdMaturity;

      return {
        fdMaturity: Math.round(fdMaturity),
        rdMaturity: Math.round(rdMaturity),
        fdInterest: Math.round(fdInterest),
        rdInterest: Math.round(rdInterest),
        fdBenefit: Math.round(fdBenefit),
        fdBetter: fdBenefit > 0,
      };
    },
    resultFields: [
      {
        key: "fdMaturity",
        label: "FD Final Amount",
        format: "currency",
      },
      {
        key: "rdMaturity",
        label: "RD Final Amount",
        format: "currency",
      },
      {
        key: "fdBenefit",
        label: "FD Advantage",
        format: "currency",
        isPrimary: true,
      }
    ],
    content: {
      whatIs:
        "FD vs RD Comparison helps you understand the trade-off between investing capital upfront (Fixed Deposit) versus building it gradually (Recurring Deposit). Mathematically, if you have a lumpsum amount available now, investing it entirely in an FD beats spreading it monthly via RD because earlier deposits compound longer. However, life isn't always mathematical: many people don't have lumpsum but earn monthly income, making RD the practical choice. This calculator shows side-by-side maturity comparison: FD scenario (₹5L invested now for 5 years) vs RD scenario (₹83.3K/month for 5 years), demonstrating FD's compounding advantage. But it also teaches that RD is NOT 'inferior'—it's ideal for savers without capital, offers systematic discipline, and provides reasonable returns. Many investors use a blended strategy: invest bonus/accumulated savings as FD, plus monthly salary portion as RD. The real insight: both FD and RD are safe (DICGC insured), guaranteed-return instruments. The difference is your circumstances, not the scheme quality. FD for capital deployment flexibility; RD for income-constrained but disciplined savers.",
      faqs: [
        {
          question:
            "If I have ₹5L, should I do FD or RD?",
          answer:
            "FD. Invest entire ₹5L immediately, lock for 3-5 years. Longer compounding time = more interest. RD is for people earning monthly, no lumpsum available.",
        },
        {
          question:
            "What if I have ₹5L but want to deploy gradually?",
          answer:
            "Ladder FDs: ₹1L each year for 5 years. Gets benefits of: (a) rupee-cost averaging (interest rates may vary), (b) liquidity (some matures each year), (c) simpler than RD.",
        },
        {
          question:
            "Is RD bad compared to FD?",
          answer:
            "Not bad, just different. RD is excellent if you don't have lumpsum. Saves ₹10K/month systematically at 5-6% return = better than keeping in savings account (2-4%).",
        },
        {
          question:
            "What's the best strategy: FD or RD or both?",
          answer:
            "Best: lumpsum from bonus/sale → FD. Monthly salary → RD. Combination leverages both: growth (FD on lumpsum) + discipline (RD on salary).",
        },
        {
          question:
            "If my salary is variable, should I use RD?",
          answer:
            "RD allows flexible deposits (some months more, some less). Or use RD with minimum, supplement FD with bonus. Flexibility is RD's advantage.",
        }
      ],
      sections: [
        {
          title: "When FD is Better: Lumpsum Available",
          body: "You have ₹5L bonus, inheritance, or sale proceeds. Invest entire ₹5L as FD immediately for 5 years. FD compounds the ₹5L for 60 months, generating maximum interest. Don't split it or delay—invest immediately to capture compounding from day one. FD is the clear winner when capital is available.",
        },
        {
          title: "When RD is Better: Monthly Savings Only",
          body: "You earn ₹1L monthly salary but no lumpsum. You can invest ₹10K/month via RD (₹60K/year) while living on remaining ₹90K. Over 5 years, ₹10K/month RD accumulates ₹6L+ with interest. Without RD, you can't save systematically from income. RD is not just 'better'—it's the ONLY practical option.",
        },
        {
          title: "The Math: Why Earlier Compounding Matters",
          body: "₹1L invested today (month 1) at 6% for 60 months compounds to ₹1.33L. Same ₹1L invested at month 60 (now) stays ₹1L. The difference is ₹33K from compounding over 60 months. This is why FD (lumpsum) beats RD (monthly deposits) when comparing total maturity.",
        },
        {
          title: "Blended Strategy: FD + RD Combined",
          body: "Invest bonus/extra as FD (₹3L now) + monthly salary (₹10K/month) as RD. This maximizes returns (FD compounding) while ensuring systematic savings (RD discipline). By year 5: FD ₹3L → ₹4L, RD ₹10K/month → ₹63K, total ≈ ₹4.63L. Better than FD alone (₹4L) or RD alone (₹63K).",
        },
        {
          title: "Psychological Difference: Discipline vs Opportunity",
          body: "FD requires self-discipline NOT to spend lumpsum immediately. RD enforces discipline automatically (salary deducted). Many people prefer RD's forced savings over FD's temptation. Choose based on your financial behavior: if you tend to spend windfalls, RD is safer.",
        },
      ],
      relatedSlugs: [
        "savings/fd-calculator",
        "savings/rd-calculator",
        "savings/fd-ladder-calculator",
      ],
    },
    }),

  // 14. FD Ladder Calculator
  createEMITool({
    slug: "fd-ladder-calculator",
    category: "savings",
    title: "FD Ladder Strategy Calculator",
    shortTitle: "FD Ladder",
    description:
      "Calculate returns from FD laddering strategy (staggered maturities)",
    keywords: [
      "fd ladder",
      "laddering strategy",
      "staggered maturity",
      "fd planning",
      "liquidity planning",
    ],
    inputs: [
      {
        type: "number",
        key: "totalAmount",
        label: "Total Amount to Invest",
        prefix: "₹",
        min: 100000,
        max: 1000000000,
        step: 100000,
        default: 500000,
      },
      {
        type: "number",
        key: "ladderYears",
        label: "Ladder Span (Years)",
        suffix: "years",
        min: 1,
        max: 10,
        step: 1,
        default: 5,
      },
      {
        type: "number",
        key: "rate",
        label: "FD Interest Rate",
        suffix: "% p.a.",
        min: 1,
        max: 15,
        step: 0.1,
        default: 6.5,
      }
    ],
    calculate(v) {
      const total = v.totalAmount as number;
      const years = v.ladderYears as number;
      const rate = (v.rate as number) / 100;

      const perRung = total / years;
      let totalMaturity = 0;
      let totalInterest = 0;

      for (let y = 1; y <= years; y++) {
        const maturity =
          perRung *
          Math.pow(1 + rate / 4, 4 * y);
        totalMaturity += maturity;
        totalInterest += maturity - perRung;
      }

      // Compare to single FD
      const singleFDMaturity = total * Math.pow(1 + rate / 4, 4 * years);
      const singleFDInterest = singleFDMaturity - total;

      return {
        totalInvested: Math.round(total),
        ladderMaturity: Math.round(totalMaturity),
        ladderInterest: Math.round(totalInterest),
        singleFDMaturity: Math.round(singleFDMaturity),
        singleFDInterest: Math.round(singleFDInterest),
        liquidityBenefit: "One rung matures each year",
      };
    },
    resultFields: [
      {
        key: "ladderMaturity",
        label: "Total Ladder Maturity",
        format: "currency",
        isPrimary: true,
      },
      {
        key: "ladderInterest",
        label: "Total Interest (Ladder)",
        format: "currency",
      },
      {
        key: "singleFDMaturity",
        label: "Single FD Maturity (Comparison)",
        format: "currency",
      }
    ],
    content: {
      whatIs:
        "FD Ladder Strategy is an advanced savings technique for investors with larger capital who want both growth and liquidity without abandoning lock-in discipline. Instead of investing ₹50L as a single 5-year FD, you divide it into 5 equal parts (₹10L each) and invest each part for different maturities (1 year, 2 years, 3 years, 4 years, 5 years). Result: every year, one FD matures, providing access to ₹10L without breaking any FD or suffering early-withdrawal penalties. At maturity, reinvest the maturing FD for 5 years again, maintaining the ladder structure. This approach offers three benefits: (1) Liquidity—₹10L available annually for emergencies or opportunities, (2) Rate averaging—FDs maturing in year 2 capture different rate environment than year 5, hedging interest-rate risk, (3) Reduced emergency fund need—since annual maturity provides cash, you need less emergency corpus elsewhere. The trade-off: slightly lower overall returns vs a single lumpsum 5-year FD (early investments in a ladder earn for fewer years), but the liquidity and psychological comfort often outweigh this ~0.5-1% difference. FD laddering is especially popular among business owners, investors near retirement, and those seeking balance between returns and access.",
      faqs: [
        {
          question:
            "Should I do FD ladder or single FD?",
          answer:
            "If you need liquidity/flexibility, ladder. If you can lock capital, single FD is better (slightly higher return due to longer compounding). Most investors with jobs: ladder is prudent.",
        },
        {
          question:
            "How does FD ladder help in rising rate environment?",
          answer:
            "As rates rise, FDs maturing later get locked at higher rates. Ladder captures rising rates gradually. Single FD locks 5-year rate immediately; if rates spike, you lose.",
        },
        {
          question:
            "What if rates fall in a ladder?",
          answer:
            "Later rungs lock lower rates. But overall, ladder averages rates—some high, some low. This is 'natural hedging.' Single FD at high rate is better if you lock before rates fall.",
        },
        {
          question:
            "How many rungs should my ladder have?",
          answer:
            "3-5 rungs typical. 5-year ladder: ₹1L per rung (1yr, 2yr, 3yr, 4yr, 5yr). Annual maturity + reallocation is manageable. More rungs = more tracking overhead.",
        },
        {
          question:
            "What do I do when a rung matures?",
          answer:
            "Reinvest in longest-term FD (e.g., 5yr). This maintains ladder structure and captures latest rates. Or use for expenses; depends on your liquidity needs.",
        }
      ],
      sections: [
        {
          title: "Structure: Creating Your FD Ladder",
          body: "Divide corpus equally: ₹50L → ₹10L rungs. Invest: ₹10L for 1yr, ₹10L for 2yr, ₹10L for 3yr, ₹10L for 4yr, ₹10L for 5yr. (Or: ₹12.5L each for 4 rungs if prefer 4-rung ladder). At each maturity: withdraw and plan, or reinvest for 5yr. Maintain equal spacing for predictable annual cash flow.",
        },
        {
          title: "Liquidity Advantage Over Single FD",
          body: "Single ₹50L FD: locked 5 years, available only at maturity. Ladder: ₹10L available yearly (years 1-5). This provides flexibility for business opportunities, children's education, or unexpected expenses without breaking FD. Many business owners prefer ladder for this reason.",
        },
        {
          title: "Rate Averaging Hedge",
          body: "Interest rates fluctuate. Laddering spreads rate risk: 1-year FD locked at today's rate (say 6%), 2-year at 6.2%, 5-year at 6.5%. If rates rise after year 3, your later rungs capture higher rates. If rates fall, earlier rungs are protected at higher rates. Net result: average rate across all rungs hedges interest-rate volatility.",
        },
        {
          title: "Reduced Emergency Fund Paradox",
          body: "Traditional advice: keep 6-month expenses as emergency fund (₹5-10L for many). With FD ladder, you already have ₹10L maturing annually. So emergency fund needs decrease—you can deploy more capital into ladder. Example: ₹60L corpus: ₹50L as ladder + ₹10L emergency fund = better efficiency than ₹50L single FD + ₹10L separate emergency fund.",
        },
        {
          title: "Reinvestment at Maturity: Maintaining the Ladder",
          body: "When year-1 FD (₹10L) matures, reinvest immediately for 5 years. This rebuilds the ladder at the latest rate. Your ladder structure perpetually maintains: always ₹10L available annually. Over decades, this becomes automatic wealth management without active decision-making.",
        },
      ],
      relatedSlugs: [
        "savings/fd-calculator",
        "savings/fd-vs-rd-comparison",
        "savings/fd-interest-calculator",
      ],
    },
    }),

  // 15. Bank Interest Comparison
  createEMITool({
    slug: "bank-interest-comparison",
    category: "savings",
    title: "Bank Interest Rate Comparison Tool",
    shortTitle: "Interest Comparison",
    description:
      "Compare interest rates and maturity across different banks and tenures",
    keywords: [
      "bank comparison",
      "interest rate comparison",
      "best fd rate",
      "highest return",
    ],
    inputs: [
      {
        type: "number",
        key: "investmentAmount",
        label: "Investment Amount",
        prefix: "₹",
        min: 100000,
        max: 100000000,
        step: 100000,
        default: 500000,
      },
      {
        type: "number",
        key: "bank1Rate",
        label: "Bank A Interest Rate",
        suffix: "% p.a.",
        min: 1,
        max: 15,
        step: 0.1,
        default: 6.5,
      },
      {
        type: "number",
        key: "bank2Rate",
        label: "Bank B Interest Rate",
        suffix: "% p.a.",
        min: 1,
        max: 15,
        step: 0.1,
        default: 6.75,
      },
      {
        type: "number",
        key: "bank3Rate",
        label: "Bank C Interest Rate",
        suffix: "% p.a.",
        min: 1,
        max: 15,
        step: 0.1,
        default: 6.3,
      },
      {
        type: "number",
        key: "years",
        label: "Tenure",
        suffix: "years",
        min: 1,
        max: 20,
        step: 1,
        default: 3,
      }
    ],
    calculate(v) {
      const amount = v.investmentAmount as number;
      const rate1 = (v.bank1Rate as number) / 100;
      const rate2 = (v.bank2Rate as number) / 100;
      const rate3 = (v.bank3Rate as number) / 100;
      const years = v.years as number;

      const bank1Maturity = amount * Math.pow(1 + rate1 / 4, 4 * years);
      const bank2Maturity = amount * Math.pow(1 + rate2 / 4, 4 * years);
      const bank3Maturity = amount * Math.pow(1 + rate3 / 4, 4 * years);

      const bank1Interest = bank1Maturity - amount;
      const bank2Interest = bank2Maturity - amount;
      const bank3Interest = bank3Maturity - amount;

      const bestBankMaturity = Math.max(
        bank1Maturity,
        bank2Maturity,
        bank3Maturity
      );
      const bestBankInterest = bestBankMaturity - amount;

      return {
        bank1Maturity: Math.round(bank1Maturity),
        bank1Interest: Math.round(bank1Interest),
        bank2Maturity: Math.round(bank2Maturity),
        bank2Interest: Math.round(bank2Interest),
        bank3Maturity: Math.round(bank3Maturity),
        bank3Interest: Math.round(bank3Interest),
        bestMaturity: Math.round(bestBankMaturity),
        bestInterest: Math.round(bestBankInterest),
        investmentAmount: amount,
      };
    },
    resultFields: [
      {
        key: "bestMaturity",
        label: "Best Bank Maturity",
        format: "currency",
        isPrimary: true,
      },
      {
        key: "bestInterest",
        label: "Best Bank Interest",
        format: "currency",
      },
      {
        key: "bank1Maturity",
        label: "Bank A Maturity",
        format: "currency",
      },
      {
        key: "bank2Maturity",
        label: "Bank B Maturity",
        format: "currency",
      }
    ],
    content: {
      whatIs:
        "Bank Interest Rate Comparison tool highlights a critical but often-overlooked factor in FD investing: banks offer different rates for the same tenure, and these differences compound to significant money over time. On ₹10 lakh FD for 5 years, a mere 0.5% rate difference (6.5% vs 7%) compounds to ₹2.5-3 lakh difference in final maturity. Many savers default to their primary bank (out of inertia), missing superior rates elsewhere. This calculator lets you compare up to 3 banks simultaneously, revealing the 'best bank for your money.' In India's competitive banking landscape, rates vary not just by bank but by tenure, customer type (senior citizen gets 0.5% extra), and deposit amount (premium deposits ₹10L+ sometimes get higher rates). RBI FD rules (DICGC insurance up to ₹5L per bank) encourage rate shopping—you can split large FDs across multiple banks for both safety AND returns. The practice of 'bank shopping' before investing in FD is standard among savvy investors. Many dedicated investors monitor FD rates across 8-10 banks weekly on aggregator websites, updating their allocation based on rate changes. This calculator simplifies that process for quick comparisons.",
      faqs: [
        {
          question:
            "How much does 0.5% rate difference matter?",
          answer:
            "On ₹10L for 5 years: 0.5% difference = ~₹2.5L difference in final amount. That's substantial! Always compare rates across 5-10 banks before investing.",
        },
        {
          question:
            "Should I prioritize bank safety or higher rate?",
          answer:
            "Safety first (all DICGC-insured banks are safe). Among safe banks, choose highest rate. Private banks (HDFC, ICICI, Axis) offer 0.5% higher; public banks safer for large amounts.",
        },
        {
          question:
            "Do newer banks offer better FD rates?",
          answer:
            "Sometimes, to attract deposits. But check: are they DICGC-insured? Sound financial health? Higher rate is pointless if bank fails. Stick to established banks with 0.25% less rate.",
        },
        {
          question:
            "Should I split FD across multiple banks?",
          answer:
            "Yes, if total >₹5L. DICGC insures max ₹5L per bank. So split ₹5L each across 3 banks. Also diversifies rate risk: some banks may change rates.",
        },
        {
          question:
            "How often should I review rates for new FDs?",
          answer:
            "Monthly. FD rates update constantly (daily even). Check before each new investment. Set calendar reminder to review rates quarterly at minimum.",
        }
      ],
      sections: [
        {
          title: "Rate Differences Compound Significantly",
          body: "Small percentage differences seem trivial but compound heavily. 6.5% vs 7% annually on ₹1L: 10-year difference = ₹40K+. On ₹50L: ₹200K+ difference. This is why comparing rates is as important as choosing the scheme. Never invest ₹1L+ in FD without checking at least 5 banks' rates first.",
        },
        {
          title: "Where to Find FD Rates?",
          body: "Visit each bank's website (deposit rates section) or use aggregator platforms (BankBazaar, ET Money, etc.). Most public banks (SBI, PNB) publish rates daily. Private banks (HDFC, ICICI, Axis) update weekly. Post office rates change quarterly (fixed by RBI). Note rates, enter in this calculator, choose the best option.",
        },
        {
          title: "Premium Deposits & Senior Citizen Rates",
          body: "Some banks offer premium FD rates for deposits ₹10L+ (0.25% higher) or for senior citizens 60+ (0.5-1% higher). If you qualify (senior citizen or large deposit), rates become even more competitive. Many seniors shop exclusively among senior-focused FDs to maximize returns.",
        },
        {
          title: "DICGC Safety vs Rate Trade-off",
          body: "All DICGC-insured banks are equally safe (up to ₹5L). Choosing a marginally lower-rated but safer bank (SBI) vs a riskier newer bank offering 0.5% higher is a judgment call. General rule: safety first (all big banks are safe), then optimize for rate among safe options. Never sacrifice DICGC coverage for an extra 0.25% return.",
        },
        {
          title: "Splitting FDs Across Banks for Rate & Safety",
          body: "With ₹20L to invest: split ₹5L each across 4 banks offering best rates. Each ₹5L is DICGC insured; collectively, you get ₹20L safety. Additionally, you capture the best rate at each bank (Bank A: 6.5%, Bank B: 6.75%, Bank C: 7%, Bank D: 6.8%). Rate shopping across banks is the optimal strategy for large FD investors.",
        },
      ],
      relatedSlugs: [
        "savings/fd-calculator",
        "savings/fd-ladder-calculator",
        "savings/fd-vs-rd-comparison",
      ],
    },
    }),

  // 16. Inflation vs FD
  createEMITool({
    slug: "inflation-vs-fd",
    category: "savings",
    title: "Inflation vs FD Real Return Calculator",
    shortTitle: "Real Return vs Inflation",
    description:
      "Calculate FD real return after accounting for inflation impact",
    keywords: [
      "inflation impact",
      "real return",
      "inflation vs fd",
      "purchasing power",
      "inflation erosion",
    ],
    inputs: [
      {
        type: "number",
        key: "fdAmount",
        label: "FD Investment",
        prefix: "₹",
        min: 100000,
        max: 100000000,
        step: 100000,
        default: 500000,
      },
      {
        type: "number",
        key: "fdRate",
        label: "FD Interest Rate",
        suffix: "% p.a.",
        min: 1,
        max: 15,
        step: 0.1,
        default: 6,
      },
      {
        type: "number",
        key: "inflationRate",
        label: "Expected Inflation Rate",
        suffix: "%",
        min: 0,
        max: 20,
        step: 0.5,
        default: 5,
      },
      {
        type: "number",
        key: "years",
        label: "Time Period",
        suffix: "years",
        min: 1,
        max: 50,
        step: 1,
        default: 5,
      }
    ],
    calculate(v) {
      const fd = v.fdAmount as number;
      const fdRate = (v.fdRate as number) / 100;
      const inflation = (v.inflationRate as number) / 100;
      const years = v.years as number;

      // Nominal maturity
      const nominalMaturity = fd * Math.pow(1 + fdRate / 4, 4 * years);
      const nominalInterest = nominalMaturity - fd;

      // Real maturity (adjusted for inflation)
      const inflationFactor = Math.pow(1 + inflation, years);
      const realValue = nominalMaturity / inflationFactor;
      const realGain = realValue - fd;

      const nominalReturn =
        ((nominalMaturity / fd - 1) * 100);
      const realReturn = ((realValue / fd - 1) * 100);

      return {
        fdInvestment: fd,
        nominalMaturity: Math.round(nominalMaturity),
        nominalInterest: Math.round(nominalInterest),
        nominalReturn: Math.round(nominalReturn * 100) / 100,
        realValue: Math.round(realValue),
        realGain: Math.round(realGain),
        realReturn: Math.round(realReturn * 100) / 100,
        inflationEroded: Math.round(nominalMaturity - realValue),
      };
    },
    resultFields: [
      {
        key: "realValue",
        label: "Real Value (After Inflation)",
        format: "currency",
        isPrimary: true,
      },
      {
        key: "nominalMaturity",
        label: "Nominal Maturity",
        format: "currency",
      },
      {
        key: "inflationEroded",
        label: "Wealth Lost to Inflation",
        format: "currency",
      }
    ],
    content: {
      whatIs:
        "Inflation vs FD Real Return Calculator reveals a critical truth in personal finance: nominal returns (what banks advertise) are misleading without adjusting for inflation. Your ₹5 lakh FD earning 6% nominally for 5 years seems to grow to ₹6.69 lakh. But if inflation runs at 5% yearly, the purchasing power of money decreases—that ₹6.69 lakh will buy what only ₹5.24 lakh would buy today. Your real return (after inflation adjustment) is merely 1% annually, not 6%. This 'inflation erosion' is often ignored by savers but deeply impacts long-term wealth. FD at 6% with 5% inflation protects capital (you're ahead of inflation) but doesn't create wealth. To build real wealth, you need returns significantly exceeding inflation: equity (12-15% nominal = 7-10% real at 5% inflation) or real estate (8% nominal = 3% real). India's inflation averages 5-6% annually (2020-2024), so FD returns (5-7%) barely keep pace. This calculator is essential for retirees and elderly investors: pure FD portfolio doesn't protect against inflation erosion over 20-30 years. Many retirees impoverish themselves unknowingly by holding 100% FD, gradually losing purchasing power despite 'returns.' Hence the financial planning maxim: allocate 40-50% FD (safety), 50-60% equity (inflation hedge) even in retirement.",
      faqs: [
        {
          question:
            "Does my FD actually lose money in inflation?",
          answer:
            "Not lose, but value decreases. ₹5L FD growing 6% nominally becomes worth ~₹3.9L in purchasing power (at 5% inflation). You're still ahead nominally but behind inflation.",
        },
        {
          question:
            "Is FD worth it if real return is only 1%?",
          answer:
            "Yes, for emergency fund (1% real return > keeping cash which is 0% after inflation). For wealth growth: no. Use FD for safety/liquidity only, equity for real growth.",
        },
        {
          question:
            "What investment beats inflation most?",
          answer:
            "Equity: 12% nominal, real 6-7% (after 5-6% inflation). Real estate: 8% nominal, real 2-3%. Gold: 5% nominal, real 0% (inflation hedge, not growth). Equity is best inflation fighter.",
        },
        {
          question:
            "Should seniors avoid FD due to inflation?",
          answer:
            "Not entirely. Seniors need safety > growth. 40% FD (for stability), 60% equity (for inflation protection). Pure FD in retirement is risky due to inflation.",
        },
        {
          question:
            "What if inflation spikes to 8-10%?",
          answer:
            "6% FD becomes 0% or negative real return. This happened in 2022. Always plan for scenario: return minus high inflation. Keep some equity allocation for such scenarios.",
        }
      ],
      sections: [
        {
          title: "Nominal vs Real Return: The Critical Difference",
          body: "Nominal return: what bank shows you (6% FD return = ₹30K interest on ₹5L). Real return: what you actually earn after inflation adjusts purchasing power (6% nominal minus 5% inflation = 1% real). Real return is what matters for actual wealth building. Savers focusing on nominal returns mislead themselves about true growth.",
        },
        {
          title: "How Inflation Erodes Wealth Over Time",
          body: "At 5% inflation, ₹1L today is worth ₹78K in year 5 (purchasing power). Your ₹5L FD grows nominally to ₹6.69L after 5 years at 6%, but in today's money, it's worth ~₹5.24L. You've earned ₹240K nominal interest but only ₹240K real (after inflation adjustment). Over 20 years, erosion becomes catastrophic: ₹1L today is worth ₹37K.",
        },
        {
          title: "Safe Investments Don't Mean Good Investments",
          body: "FD is safe (DICGC insured, capital protected) but doesn't beat inflation long-term. Equities are volatile (short-term losses possible) but beat inflation (12-15% nominal = 7-10% real). Wisdom: combine both—safety (FD) for capital preservation, equity (stocks/MFs) for real wealth growth. Pure FD retirees experience gradual impoverishment.",
        },
        {
          title: "Real Return Planning for Retirement",
          body: "Retirees often plan with FD returns (6% nominal) but forget inflation. If you need ₹50K/month now and inflation is 5%, you'll need ₹63K/month in 10 years (5% annually compounded). Pure FD-generated income can't keep pace. Include equity allocation (20-30%) for inflation-beating growth in retirement portfolio.",
        },
        {
          title: "Scenario: What Inflation Spike Does",
          body: "2022-23 inflation in India: 7-8%. FDs offered 6-6.5%, resulting in negative real returns. Savers earning 6% felt their capital was shrinking in real terms. If high inflation persists, adjust portfolio: reduce FD, increase equity. Real asset allocation requires monitoring inflation expectations and adjusting returns accordingly.",
        },
      ],
      relatedSlugs: [
        "investment/real-return-calculator",
        "investment/inflation-adjusted-return",
        "savings/fd-calculator",
      ],
    },
    }),

  // 17. Post Office Savings Account
  createEMITool({
    slug: "post-office-savings",
    category: "savings",
    title: "Post Office Savings Account Returns",
    shortTitle: "PO Savings Returns",
    description:
      "Calculate returns from Post Office Savings Account with 4% interest",
    keywords: [
      "post office savings",
      "post office account",
      "small savings",
      "government savings",
    ],
    inputs: [
      {
        type: "number",
        key: "monthlyDeposit",
        label: "Monthly Deposit",
        prefix: "₹",
        min: 100,
        max: 1000000,
        step: 100,
        default: 5000,
      },
      {
        type: "number",
        key: "months",
        label: "Duration",
        suffix: "months",
        min: 1,
        max: 600,
        step: 1,
        default: 60,
      }
    ],
    calculate(v) {
      const monthly = v.monthlyDeposit as number;
      const months = v.months as number;
      const annualRate = 0.04; // 4% current rate
      const monthlyRate = annualRate / 12;

      let balance = 0;
      for (let m = 1; m <= months; m++) {
        balance = balance * (1 + monthlyRate) + monthly;
      }

      const totalDeposited = monthly * months;
      const interest = balance - totalDeposited;

      return {
        totalDeposited: Math.round(totalDeposited),
        totalBalance: Math.round(balance),
        interestEarned: Math.round(interest),
        months: months,
        years: Math.round((months / 12) * 10) / 10,
        monthlyDeposit: Math.round(monthly),
      };
    },
    resultFields: [
      {
        key: "totalBalance",
        label: "Final Balance",
        format: "currency",
        isPrimary: true,
      },
      {
        key: "totalDeposited",
        label: "Total Deposited",
        format: "currency",
      },
      {
        key: "interestEarned",
        label: "Interest Earned",
        format: "currency",
      }
    ],
    content: {
      whatIs:
        "Post Office Savings Account is India's most accessible government savings account, available at any of 400,000+ post offices nationwide. With zero paperwork and minimal documentation, you can open an account within 30 minutes. The scheme offers 4% annual interest (compounded and credited quarterly), fully liquid access (withdraw anytime without penalty), and absolute safety (government-backed DICGC insurance). There's no minimum balance, no lock-in period, and no maturity date—money stays forever. While 4% return seems modest compared to FD (6-7%) or RD (5-6%), post office savings serves a critical role: emergency fund and children's first savings. Many parents open PO savings account for newborns, teaching the habit of saving from childhood. Homemakers, informal workers, and rural population prefer Post Office Savings for its accessibility and safety. Combined with PO FD, NSC, or SCSS schemes, Post Office Savings creates a complete savings portfolio. The primary advantage: maximum accessibility and government endorsement, making it perfect for financially unsophisticated savers or those in remote areas without easy bank access. Think of it as India's most democratic savings instrument—everyone from richest to poorest can access it.",
      faqs: [
        {
          question:
            "Is Post Office Savings Account safe?",
          answer:
            "Extremely safe. Government-backed, DICGC insurance up to ₹5L. Any post office can open. Perfect for small savers/children.",
        },
        {
          question:
            "Should I use PO Savings or regular bank savings?",
          answer:
            "PO Savings: 4%, no-frills. Bank savings: 2-4%. Both similar. PO is good if you're near post office; bank better for convenience. RD is better than either for same amount.",
        },
        {
          question:
            "Can I withdraw PO Savings anytime?",
          answer:
            "Yes, fully liquid. No lock-in, no penalty. Withdraw any amount anytime. Perfect as emergency fund alongside FD.",
        },
        {
          question:
            "Is interest taxed on PO Savings?",
          answer:
            "Yes, taxable as income. At 4% on ₹5L/year = ₹20K interest (fully taxable). Senior citizens benefit from 80TTB deduction (up to ₹50K).",
        },
        {
          question:
            "What's better for children: PO Savings or Sukanya Samriddhi?",
          answer:
            "For daughters: SSY (8.2%, 21-year lock, highest). For sons/general: PO Savings (4%, liquid, simple). Use SSY for girls if you can lock capital.",
        }
      ],
      sections: [
        {
          title: "When to Use PO Savings Account",
          body: "Emergency fund: ₹5L sitting in PO Savings earns 4% safely. Children's pocket money/small savings: ₹500-1000/year grows with interest. Informal workers/daily wage earners: accessible, government-backed, no minimum balance. Village post offices: only savings account available in many rural areas. Perfect as 'first account' before graduating to FD/RD/investments.",
        },
        {
          title: "PO Savings vs Bank Savings Account",
          body: "PO Savings: 4%, government DICGC backed, accessible. Bank Savings: 3-4%, bank DICGC backed, easier ATM access. Both safe. Choice: proximity and convenience. Urban savers prefer bank (ATM availability). Rural savers prefer PO (accessibility). On interest, marginal difference (0-1%) doesn't matter given safety parity.",
        },
        {
          title: "Why 4% is 'Low' But Still Beats Inflation",
          body: "At 5% inflation, 4% PO Savings return is slightly negative in real terms. But compared to keeping ₹1L at home (0% return, 100% inflation erosion), PO Savings is vastly superior. Emergency fund needs stability, not growth—4% beats inflation partially, serves purpose of value preservation.",
        },
        {
          title: "Complete Post Office Portfolio Strategy",
          body: "For average Indian saver: ₹2L in PO Savings (emergency fund, liquid) + ₹1.5L in PPF (highest priority) + ₹1.5L in FD/RD (flexibility) = ₹5L deployed safely, tax-efficiently. Add SSY if have daughters. This structure combines accessibility (PO Savings), tax-efficiency (PPF), and returns (RD/FD).",
        },
        {
          title: "Accessibility Advantage for Rural/Remote Areas",
          body: "Post offices are ubiquitous in rural India. PO Savings account can be opened without visiting bank, usually without ID proof requirement (only basic details). Perfect for inclusive finance: homemakers, farmers, informal workers who might not have bank access. Government's 'post office for everyone' mission.",
        },
      ],
      relatedSlugs: [
        "savings/rd-calculator",
        "savings/fd-calculator",
        "savings/ssy-calculator",
      ],
    },
    }),

  // 18. Cumulative vs Non-Cumulative FD
  createEMITool({
    slug: "cumulative-vs-non-cumulative-fd",
    category: "savings",
    title: "Cumulative vs Non-Cumulative FD Comparison",
    shortTitle: "Cumulative vs Non-Cumulative",
    description:
      "Compare cumulative FD (reinvest interest) vs non-cumulative FD (interest payout)",
    keywords: [
      "cumulative fd",
      "non-cumulative fd",
      "monthly interest",
      "interest payout",
    ],
    inputs: [
      {
        type: "number",
        key: "fdAmount",
        label: "FD Investment",
        prefix: "₹",
        min: 100000,
        max: 100000000,
        step: 100000,
        default: 500000,
      },
      {
        type: "number",
        key: "fdRate",
        label: "FD Interest Rate",
        suffix: "% p.a.",
        min: 1,
        max: 15,
        step: 0.1,
        default: 6.5,
      },
      {
        type: "number",
        key: "years",
        label: "FD Tenure",
        suffix: "years",
        min: 1,
        max: 20,
        step: 1,
        default: 5,
      }
    ],
    calculate(v) {
      const amount = v.fdAmount as number;
      const rate = (v.fdRate as number) / 100;
      const years = v.years as number;

      // Cumulative: interest reinvested
      const cumulativeMaturity = amount * Math.pow(1 + rate / 4, 4 * years);
      const cumulativeInterest = cumulativeMaturity - amount;

      // Non-cumulative: interest paid out (let's assume annually paid, not reinvested)
      const monthlyRate = rate / 12;
      let nonCumulativeBalance = amount;
      let totalPaidOut = 0;

      for (let m = 1; m <= years * 12; m++) {
        const interest = nonCumulativeBalance * monthlyRate;
        totalPaidOut += interest;
        // Non-cumulative: don't reinvest interest
      }

      // At maturity: principal + all interest paid out
      const nonCumulativeTotalReceived = amount + totalPaidOut;

      return {
        cumulativeMaturity: Math.round(cumulativeMaturity),
        cumulativeInterest: Math.round(cumulativeInterest),
        nonCumulativeMaturity: Math.round(nonCumulativeTotalReceived),
        nonCumulativeInterest: Math.round(totalPaidOut),
        cumulativeBenefit: Math.round(
          cumulativeMaturity - nonCumulativeTotalReceived
        ),
      };
    },
    resultFields: [
      {
        key: "cumulativeMaturity",
        label: "Cumulative FD Maturity",
        format: "currency",
        isPrimary: true,
      },
      {
        key: "nonCumulativeMaturity",
        label: "Non-Cumulative FD Maturity",
        format: "currency",
      },
      {
        key: "cumulativeBenefit",
        label: "Cumulative Advantage",
        format: "currency",
      }
    ],
    content: {
      whatIs:
        "Cumulative vs Non-Cumulative FD represents a fundamental choice in your FD strategy: do you want maximum growth (cumulative) or regular income (non-cumulative)? In a Cumulative FD, interest is automatically reinvested—you earn interest on interest, accelerating compounding. In a Non-Cumulative FD (monthly/quarterly interest payout), interest is paid directly to your bank account as regular income, while principal stays locked. The difference is substantial: ₹5L cumulative FD at 6.5% for 5 years grows to ₹6.69L (₹1.69L gain). Same amount as non-cumulative generates ₹1.63L total interest paid out over 5 years, but maturity is only ₹5L (no reinvestment). The extra ₹60K from cumulative comes purely from interest compounding. Which to choose depends on your life stage and cash needs. Young professionals building wealth: cumulative (maximize growth). Retirees needing monthly living expenses: non-cumulative (regular cash flow). Many investors use a blended approach: cumulative FDs for surplus capital (long-term wealth building), non-cumulative for a portion to generate monthly income. This calculator compares both scenarios with exact maturity amounts, helping you decide based on your financial goals.",
      faqs: [
        {
          question:
            "Who should choose cumulative FD?",
          answer:
            "Investors with growth goal, no income need. Wealth building phase (20s-50s). Maximize compounding. Ideal for lumpsum savings that won't be needed soon.",
        },
        {
          question:
            "Who should choose non-cumulative FD?",
          answer:
            "Retirees needing monthly income. Older investors (60+). Non-cumulative FD's payout = partial replacement for salary. Common in retirement planning.",
        },
        {
          question:
            "Is cumulative FD better?",
          answer:
            "For growth: yes, ~5-10% higher maturity over 5 years. For income planning: no. For lifestyle: you need cash flow (non-cumulative). Choose based on your goal.",
        },
        {
          question:
            "Can I switch from non-cumulative to cumulative?",
          answer:
            "Not directly. You'd need to break the FD and reinvest. Not recommended (penalty applies). Decide at purchase time based on your needs.",
        },
        {
          question:
            "What about tax impact of cumulative vs non-cumulative?",
          answer:
            "Both: interest is fully taxable. Cumulative: TDS on maturity. Non-cumulative: TDS on each interest payout. Tax-wise similar, but cumulative allows larger TDS once (if possible).",
        }
      ],
      sections: [
        {
          title: "Cumulative for Wealth Builders (Age 25-50)",
          body: "You don't need monthly interest; you want maximum compounding. Cumulative FD: interest reinvests, grows exponentially. Over 20 years, cumulative beats non-cumulative by ₹50-100L on large corpus. Lock it, forget it, let compounding do the work. Ideal if you have pension, salary, other income sources.",
        },
        {
          title: "Non-Cumulative for Retirees & Income Seekers (Age 60+)",
          body: "You need ₹1L corpus to generate ₹6-7K monthly income (post-retirement). Non-cumulative FD paying monthly interest provides cash flow for expenses. ₹30L corpus generates ₹20.5K monthly (8.2% SCSS) or ₹15-16K monthly (6% FD). This bridges gap between pension and living expense needs. Monthly income is psychologically important.",
        },
        {
          title: "Switching Between Cumulative & Non-Cumulative",
          body: "Can't directly switch during tenure. Options: (1) Break non-cumulative FD (penalty applies), reinvest as cumulative. (2) At maturity of non-cumulative, take interest accumulated, reinvest as cumulative. (3) For new FD, choose based on current needs. Always decide at inception, not mid-tenure.",
        },
        {
          title: "Blended Strategy: Both Types Combined",
          body: "Many investors: 60% cumulative FDs (wealth building), 40% non-cumulative (income generation). Example: ₹50L total: ₹30L cumulative (grows to ₹60L in 15 years), ₹20L non-cumulative (generates ₹13K monthly). Combination addresses both goals: growth and income.",
        },
        {
          title: "Tax Consideration: TDS & Interest Accrual",
          body: "Cumulative: TDS levied at maturity (all interest at once). Non-cumulative: TDS levied on each payout. For individuals in high tax bracket with multiple FDs, cumulative might allow better TDS management (pay interest tax once vs distributed). For senior citizens, non-cumulative's distributed interest might keep each payout below TDS threshold.",
        },
      ],
      relatedSlugs: [
        "savings/fd-calculator",
        "savings/fd-interest-calculator",
        "retirement/retirement-income-calculator",
      ],
    },
    }),

  // 19. Daily Interest Calculator
  createEMITool({
    slug: "daily-interest-calculator",
    category: "savings",
    title: "Daily Interest Calculator",
    shortTitle: "Daily Compounding",
    description: "Calculate interest with daily compounding frequency",
    keywords: [
      "daily interest",
      "daily compounding",
      "frequent compounding",
      "interest frequency",
    ],
    inputs: [
      {
        type: "number",
        key: "principal",
        label: "Principal Amount",
        prefix: "₹",
        min: 1000,
        max: 100000000,
        step: 1000,
        default: 100000,
      },
      {
        type: "number",
        key: "rate",
        label: "Annual Interest Rate",
        suffix: "% p.a.",
        min: 0.1,
        max: 20,
        step: 0.05,
        default: 5,
      },
      {
        type: "number",
        key: "days",
        label: "Duration",
        suffix: "days",
        min: 1,
        max: 36500,
        step: 1,
        default: 365,
      }
    ],
    calculate(v) {
      const principal = v.principal as number;
      const annual = (v.rate as number) / 100;
      const days = v.days as number;

      const dailyRate = annual / 365;
      const maturityAmount = principal * Math.pow(1 + dailyRate, days);
      const interest = maturityAmount - principal;
      const years = Math.round((days / 365) * 10) / 10;

      return {
        principal: Math.round(principal),
        maturityAmount: Math.round(maturityAmount),
        interestEarned: Math.round(interest),
        days: days,
        years: years,
        dailyReturn: Math.round(interest / days * 100) / 100,
      };
    },
    resultFields: [
      {
        key: "maturityAmount",
        label: "Maturity Amount",
        format: "currency",
        isPrimary: true,
      },
      {
        key: "interestEarned",
        label: "Interest Earned",
        format: "currency",
      },
      { key: "dailyReturn", label: "Daily Return", format: "currency" },
    ],
    content: {
      whatIs:
        "Daily Interest Compounding is the most frequent compounding method used by banks in India, particularly for savings accounts. Unlike quarterly (FD) or annual (NSC) compounding, daily compounding calculates interest every single day and adds it to your principal, so the next day's interest is calculated on the increased amount. This creates the fastest wealth acceleration among regular compounding methods. The difference is subtle for short periods: ₹1L at 5% for 1 year grows to ₹1.05129L with daily compounding vs ₹1.05127L with quarterly compounding (₹2 difference). But over 30 years, daily compounding accumulates to 2-3% more wealth than quarterly. Many savings accounts (especially those earning 3-4% interest) use daily compounding to attract savers with competitive rates. However, the compounding frequency is often oversold as a benefit—the interest RATE matters far more than frequency. 5.5% quarterly beats 5% daily every time. This calculator lets you experiment with different timeframes and rates to understand how daily compounding accelerates wealth. Most savers can ignore this calculator (frequency doesn't move the needle), but those with large amounts in savings accounts (earning 4% daily compound) might see meaningful differences vs quarterly.",
      faqs: [
        {
          question:
            "How much does daily compounding beat quarterly?",
          answer:
            "Over 5 years at 5%: daily = 1.284x, quarterly = 1.283x (negligible difference). Over 30 years: daily = 4.48x, quarterly = 4.39x (2% difference). Adds up over time.",
        },
        {
          question:
            "Do banks really use daily compounding?",
          answer:
            "Savings accounts: yes, daily. FDs: quarterly standard (some offer monthly/annual). RDs: monthly deposits, quarterly compounding. Check your specific account terms.",
        },
        {
          question:
            "Is daily compounding worth switching banks?",
          answer:
            "No. Rate difference (0.5% higher) matters way more than frequency. 5.5% quarterly beats 5% daily every time. Prioritize rate, not frequency.",
        },
        {
          question:
            "Can I calculate daily interest manually?",
          answer:
            "Use formula: Amount = P(1 + r/365)^n. For 365 days (1 year): Amount = 100K × (1.0000137)^365 = 105.13K. Or just use this calculator—easier!",
        },
        {
          question:
            "Why not hourly or minute compounding?",
          answer:
            "Law of diminishing returns. Daily vs hourly: ~0.01% more. Practically, daily is maximum effective frequency. Beyond doesn't materially increase returns.",
        }
      ],
      sections: [
        {
          title: "Which Accounts Use Daily Compounding?",
          body: "Savings accounts (all banks): daily compounding (interest credited monthly). Money market accounts: often daily. Some FDs: daily (vs quarterly standard). RD: monthly deposits, quarterly compounding (not daily). Bonds: annual/semi-annual (not daily). When opening account, check terms: 'daily compound, interest credited monthly/quarterly.'",
        },
        {
          title: "Rate Always Beats Frequency",
          body: "5.5% quarterly compounding on ₹1L/year beats 5% daily compounding on ₹1L/year. Rate difference (0.5%) compounds to ~₹5K over 5 years. Frequency difference (daily vs quarterly) adds ~₹500 over 5 years. Lesson: shop for highest rate; frequency is bonus, not decision-maker.",
        },
        {
          title: "Time Value: Why Decades Matter",
          body: "Over 5 years: daily vs quarterly difference = ₹500-1000. Over 30 years: daily vs quarterly difference = ₹50-100K on ₹1L invested. Long-term investors benefit meaningfully from daily compounding. Savers with short-term goals (<3 years) can ignore frequency differences.",
        },
        {
          title: "Savings Account Strategy: Maximize Daily Compounding",
          body: "If you must keep emergency fund in savings account (for liquidity), use daily-compounding account. On ₹5L emergency corpus at 3.5% daily vs 3.25% quarterly = ₹3.75K annual difference. Seems small, but over 10 years = ₹50K+. Daily compounding in savings account is the 'free upgrade' most savers don't leverage.",
        },
        {
          title: "Hidden Advantage: Daily Interest on Fresh Deposits",
          body: "With daily compounding, every rupee you deposit starts earning immediately. Deposit ₹10K on 1st of month; it earns for 31 days that month. Monthly compounding would calculate interest only at month-end. This daily accrual advantage is especially valuable for short-term deposits or frequent depositors.",
        },
      ],
      relatedSlugs: [
        "savings/compound-interest-calculator",
        "savings/fd-calculator",
        "savings/simple-interest-calculator",
      ],
    },
    }),

  // 20. Rule of 72
  createEMITool({
    slug: "rule-of-72",
    category: "savings",
    title: "Rule of 72 - Doubling Calculator",
    shortTitle: "Rule of 72",
    description:
      "Calculate how long your money takes to double using the Rule of 72",
    keywords: [
      "rule of 72",
      "doubling time",
      "compound interest",
      "wealth doubling",
      "time to double",
    ],
    inputs: [
      {
        type: "number",
        key: "rate",
        label: "Annual Return Rate",
        suffix: "% p.a.",
        min: 0.1,
        max: 50,
        step: 0.5,
        default: 8,
      },
      {
        type: "number",
        key: "initialAmount",
        label: "Initial Investment",
        prefix: "₹",
        min: 1000,
        max: 100000000,
        step: 1000,
        default: 100000,
      }
    ],
    calculate(v) {
      const rate = v.rate as number;
      const initial = v.initialAmount as number;

      // Rule of 72: doubling time = 72 / rate
      const doublingYears = 72 / rate;
      const doublingMonths = Math.round((doublingYears % 1) * 12);
      const doublingYearsInt = Math.floor(doublingYears);

      // Actual calculation for verification
      const actualDoubling = Math.log(2) / Math.log(1 + rate / 100);

      // Value after doubling
      const doubledValue = initial * 2;

      // Value after 10, 20, 30 years
      const value10Y =
        initial * Math.pow(1 + rate / 100, 10);
      const value20Y =
        initial * Math.pow(1 + rate / 100, 20);
      const value30Y =
        initial * Math.pow(1 + rate / 100, 30);

      return {
        initialAmount: Math.round(initial),
        doubledValue: Math.round(doubledValue),
        doublingYears: doublingYearsInt,
        doublingMonths: doublingMonths,
        actualDoublingYears: Math.round(actualDoubling * 10) / 10,
        value10Y: Math.round(value10Y),
        value20Y: Math.round(value20Y),
        value30Y: Math.round(value30Y),
      };
    },
    resultFields: [
      {
        key: "doublingYears",
        label: "Years to Double (Rule of 72)",
        format: "number",
        isPrimary: true,
      },
      {
        key: "doubledValue",
        label: "Doubled Amount",
        format: "currency",
      },
      {
        key: "actualDoublingYears",
        label: "Actual Doubling Time",
        format: "years",
      }
    ],
    content: {
      whatIs:
        "Rule of 72 is one of finance's most elegant and practical tools: a simple mental-math formula that estimates how long your money takes to double at a given return rate. The formula: Doubling Time (years) = 72 ÷ Annual Return Rate. At 8% return, divide 72 by 8 to get 9 years—your investment doubles in 9 years. At 6% (FD rate), 72÷6 = 12 years. At 12% (equity returns), 72÷12 = 6 years. Remarkably accurate for rates between 1-15%, this rule helps savers intuitively grasp compounding power without complex calculations. The beauty: it makes wealth building concrete. 'I'll double my money in 9 years' is psychologically motivating versus abstract '8% return.' Many investors use Rule of 72 in retirement planning: starting at 25 with ₹1L at 8% = double to ₹2L in 9 years, ₹4L in 18 years, ₹8L in 27 years, ₹16L by age 52. This visualization of exponential growth motivates early and consistent investing. Understanding Rule of 72 transforms how you evaluate investments: is 1% higher return worth switching? Yes, because 8% vs 9% changes doubling time from 9 years to 8 years—one year of life given back. This calculator runs the exact math alongside Rule of 72 estimate, showing the formula's surprising accuracy across different rates and amounts.",
      faqs: [
        {
          question:
            "How accurate is Rule of 72?",
          answer:
            "Very accurate for typical return rates (5-10%). Error: <1% for 1-15% returns. Less accurate outside this range. But accurate enough for planning purposes.",
        },
        {
          question:
            "Examples: how long to double at different rates?",
          answer:
            "5% = 14.4 years. 8% = 9 years. 10% = 7.2 years. 12% = 6 years. 15% = 4.8 years. See how higher returns dramatically accelerate doubling?",
        },
        {
          question:
            "Rule of 72 vs Rule of 69?",
          answer:
            "Rule of 69: 69 / rate = doubling time (more accurate mathematically). Rule of 72: easier mental math (72 divisible by more numbers). Use 72 for quick estimates.",
        },
        {
          question:
            "Can I use Rule of 72 for negative returns?",
          answer:
            "No. Rule of 72 is for growth/doubling. For losses, it doesn't apply. Losses halve value, different calculation (halving time = 72 / decline rate).",
        },
        {
          question:
            "Why is the rule so useful?",
          answer:
            "Intuitive understanding. ₹1L at 8% = ₹2L in 9 years, ₹4L in 18 years, ₹8L in 27 years. Powers your wealth building plan without calculators. Invest early; watch it grow exponentially.",
        }
      ],
      sections: [
        {
          title: "Quick Application: Comparing Investments",
          body: "FD at 6%: 72/6 = 12 years to double. NSC at 7.7%: 72/7.7 = 9.4 years. PPF at 7.1%: 72/7.1 = 10.1 years. Equity at 12%: 72/12 = 6 years. Instant visual: equity returns double your money 6 years faster than FD. This insight drives asset allocation decisions: equity deserves allocation despite volatility.",
        },
        {
          title: "Multiple Doublings: The Power of Decades",
          body: "At 8% return, money doubles every 9 years. Over 36 years (career span age 25-61): 4 doublings = ₹1L → ₹2L → ₹4L → ₹8L → ₹16L. Over 45 years (till 70): 5 doublings = ₹32L. Starting ₹50K/year SIP compounds to multi-crore corpus through multiple doublings. This is why starting early is revolutionary.",
        },
        {
          title: "Rule of 72 Variations: Halving, Tripling",
          body: "Rule of 72 (doubling). Rule of 114 (tripling): 114/8% = 14.25 years to triple. Rule of 36 (halving with inflation): if inflation is 6%, your purchasing power halves in 72/6 = 12 years. These variants help plan for different scenarios: inflation erosion, investment growth, portfolio milestones.",
        },
        {
          title: "Psychological Impact: Making Wealth Concrete",
          body: "'8% return' is abstract. '₹1L becomes ₹2L in 9 years' is concrete and motivating. Rule of 72 transforms investment concepts into tangible time horizons. Parents using Rule of 72: 'PPF at 7.1% will grow daughter's ₹1.5L/year to ₹30L+ in 21 years' motivates consistent contributions. Concrete goals outperform abstract percentages.",
        },
        {
          title: "Accuracy & Limitations",
          body: "Rule of 72 is accurate to within 1% for rates 1-15%. At 1% return: formula says 72 years (actual ~70); error = 3%. At 20% return: formula says 3.6 years (actual ~3.8); error = 5%. For ultra-high returns (>20%) or ultra-low (<1%), use calculator instead. For normal investment scenarios, Rule of 72 is sufficient and practical.",
        },
      ],
      relatedSlugs: [
        "savings/compound-interest-calculator",
        "investment/sip-calculator",
        "investment/cagr-calculator",
      ],
    },
    }),
];
